import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select, { components as selectComponents } from 'react-select';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import '../css/ordersGlovoKooul.css';
import kooulPendingAudioSrc from '../sounds/mp3/bip_order_kooul.mp3';

import Modal from '../components/Modal';
import { header } from '../components/header.encry';
import { generateBarcodeFromValue } from '../components/barcodeHelper';
import { formatOrderTicketDateTime } from '../utils/orderTicket';
import { fetchPlateformsByStore, syncKooulOrders, fetchPlateforms } from '../components/PlateformService';
import { fetchProducts as fetchInventoryProducts } from '../components/ProductService';
import { syncGlovoStoreLive } from '../services/glovoLiveSync';

const API = process.env.REACT_APP_PEGASUS_API_URL || 'https://api.pegasus.le-celadon.ma';
const POLL_INTERVAL = 15000;
const KOOUL_SYNC_INTERVAL = 60000;
const KOOUL_PLATEFORM_PREFIX = 'kooul_';
const ALL_STORES_VALUE = '__all_active_stores__';
const REPORT_STORE_COLORS_STORAGE_KEY = 'ordersGlovo.reportStoreColors';
const REPORT_STORE_COLORS = [
  '#e11d48', '#0f766e', '#2563eb', '#d97706', '#7c3aed', '#0891b2',
  '#65a30d', '#c026d3', '#ea580c', '#4f46e5', '#059669', '#be123c',
];

const loadReportStoreColors = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(REPORT_STORE_COLORS_STORAGE_KEY) || '{}');
    return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
  } catch (error) {
    console.warn('Unable to load Glovo report store colors.', error);
    return {};
  }
};

const saveReportStoreColors = (colors) => {
  try {
    localStorage.setItem(REPORT_STORE_COLORS_STORAGE_KEY, JSON.stringify(colors));
  } catch (error) {
    console.warn('Unable to save Glovo report store colors.', error);
  }
};

const createFallbackStoreColor = (storeKey) => {
  const hash = Array.from(String(storeKey || '')).reduce((value, char) => ((value * 31) + char.charCodeAt(0)) >>> 0, 0);
  return `hsl(${hash % 360}, 68%, 43%)`;
};

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const extractOrders = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  return payload?.data || payload?.orders || payload?.response || payload?.items || [];
};

const extractPlateforms = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const possibleKeys = ['data', 'plateforms', 'response', 'items', 'results'];
  for (const key of possibleKeys) {
    if (Array.isArray(payload[key])) {
      return payload[key];
    }
  }

  return [];
};

const numberify = (value) => {
  const numericValue = typeof value === 'number' ? value : parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const resolveOrderId = (order) => {
  const value = order?._id || order?.orderidraw || order?.orderId || order?.id || '';
  if (value && typeof value === 'object') {
    return String(value.$oid || value._id || value.id || '').trim();
  }
  return String(value).trim();
};

const findTicketPayload = (payload) => {
  if (!payload) {
    return null;
  }

  const visitedObjects = new WeakSet();
  const visitedArrays = new WeakSet();
  const queue = [payload];

  while (queue.length) {
    const current = queue.shift();

    if (!current) {
      continue;
    }

    if (Array.isArray(current)) {
      if (visitedArrays.has(current)) {
        continue;
      }

      visitedArrays.add(current);
      current.forEach((entry) => {
        if (entry && (typeof entry === 'object' || Array.isArray(entry))) {
          queue.push(entry);
        }
      });

      continue;
    }

    if (typeof current !== 'object') {
      continue;
    }

    if (visitedObjects.has(current)) {
      continue;
    }

    visitedObjects.add(current);

    if (Array.isArray(current?.items) && current.items.every((item) => item && typeof item === 'object')) {
      return current;
    }

    Object.values(current).forEach((value) => {
      if (value && (typeof value === 'object' || Array.isArray(value))) {
        queue.push(value);
      }
    });
  }

  return null;
};

const buildTicketLines = (items) =>
  (Array.isArray(items) ? items : []).map((item) => {
    const quantity = numberify(item?.qty ?? item?.quantity ?? 0);
    const unitPrice = numberify(item?.price ?? item?.unit_price ?? 0);
    const total = quantity * unitPrice;
    const label = item?.label || item?.name || item?.barcode || item?.ref || '';
    const barcodeValue = item?.barcode || item?.ref || '';

    return {
      description: label,
      price: Number.isFinite(total) ? total : 0,
      quantity,
      barcodeSvg: barcodeValue ? generateBarcodeFromValue(barcodeValue) : '',
    };
  });

const computeOrderTotal = (order) => {
  const fromOrder = numberify(order?.totalPrice);
  if (fromOrder > 0) {
    return fromOrder;
  }

  return (order?.products || []).reduce((sum, product) => {
    const price = numberify(product?.price);
    const quantity = parseInt(product?.quantity, 10) || 0;
    return sum + price * quantity;
  }, 0);
};

const toStoreOption = (store) => ({
  label: store?.name || store?.storeCode || 'Magasin',
  value: store?.storeCode,
  storeId: store?._id,
  storeType: store?.store_type,
  processors: store?.processors || [],
});

const annotateOrderStore = (order, store) => ({
  ...order,
  __storeId: store?.storeId || '',
  __storeCode: store?.value || '',
  __storeName: store?.label || store?.value || 'Magasin',
});

const deduplicateOrders = (orders) => {
  const seen = new Set();
  return (Array.isArray(orders) ? orders : []).filter((order, index) => {
    const key = resolveOrderId(order) || `${order?.orderCode || ''}:${order?.orderNumber || ''}:${index}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const resolveOrderTimestamp = (order) => {
  const rawDate = String(order?.orderDate || '').trim();
  const rawTime = String(order?.orderTime || '00:00:00').trim();
  const frenchDate = rawDate.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (frenchDate) {
    const parsed = new Date(`${frenchDate[3]}-${frenchDate[2]}-${frenchDate[1]}T${rawTime}`);
    if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
  }

  const parsed = new Date(order?.createdAt || order?.created_at || rawDate);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const resolveOrderHour = (order) => {
  const timeMatch = String(order?.orderTime || '').trim().match(/^(\d{1,2}):/);
  if (timeMatch) {
    const hour = Number(timeMatch[1]);
    if (Number.isInteger(hour) && hour >= 0 && hour <= 23) return hour;
  }

  const parsed = new Date(order?.createdAt || order?.created_at || '');
  return Number.isNaN(parsed.getTime()) ? null : parsed.getHours();
};

const resolveOrderDateKey = (order) => {
  const rawDate = String(order?.orderDate || '').trim();
  const frenchDate = rawDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (frenchDate) {
    return `${frenchDate[3]}-${String(frenchDate[2]).padStart(2, '0')}-${String(frenchDate[1]).padStart(2, '0')}`;
  }

  const isoDate = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDate) return rawDate;

  const parsed = new Date(order?.createdAt || order?.created_at || rawDate);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
};

const sortOrdersNewestFirst = (orders) =>
  (Array.isArray(orders) ? orders : []).slice().sort((left, right) => resolveOrderTimestamp(right) - resolveOrderTimestamp(left));

const resolveProductImageUrl = (url) => {
  if (!url) {
    return '';
  }

  const trimmedUrl = String(url).trim();
  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  const basePath = process.env.REACT_APP_DATA_PIC_PATH || '';
  if (!basePath) {
    return trimmedUrl;
  }

  const normalizedBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
  const normalizedPath = trimmedUrl.startsWith('/') ? trimmedUrl.slice(1) : trimmedUrl;

  return `${normalizedBase}/${normalizedPath}`;
};

const createEmptyCallReviewForm = () => ({
  calling_date: '',
  review: '',
  is_liked: '',
});

const normalizeId = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    return value.$oid || value.oid || value._id || value.id || '';
  }

  return '';
};

const formatCallReviewDisplayDate = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
};

const toDatetimeLocalValue = (value) => {
  if (!value) {
    return '';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toISOString().slice(0, 16);
};

const hasReservedInventory = (order) =>
  Array.isArray(order?.inventory?.reserved) && order.inventory.reserved.length > 0;

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const getTodayInputDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const OrdersGlovo = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [inProgressOrders, setInProgressOrders] = useState([]);
  const [preorders, setPreorders] = useState([]);
  const [readyOrders, setReadyOrders] = useState([]);
  const [goneOrders, setGoneOrders] = useState([]);
  const [reportDate, setReportDate] = useState(getTodayInputDate);
  const [reportStoreColors, setReportStoreColors] = useState(loadReportStoreColors);
  const [glovoProgrammed, setGlovoProgrammed] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [orderFilterReady, setOrderFilterReady] = useState('');
  const [orderFilterGone, setOrderFilterGone] = useState('');
  const [selectedOrderForMot, setSelectedOrderForMot] = useState(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [customerModalVisible, setCustomerModalVisible] = useState(false);
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);
  const [selectedCustomerOrderCode, setSelectedCustomerOrderCode] = useState('');
  const [previewOrderNumber, setPreviewOrderNumber] = useState('');
  const [previewOrderCode, setPreviewOrderCode] = useState('');
  const [motEditorValue, setMotEditorValue] = useState('');
  const [motPrintTab, setMotPrintTab] = useState('mot');
  const [remotePrinters, setRemotePrinters] = useState([]);
  const [imagePrintPrinterUid, setImagePrintPrinterUid] = useState('');
  const [imagePrintPaperName, setImagePrintPaperName] = useState('');
  const [imagePrintFile, setImagePrintFile] = useState(null);
  const [imagePrintPreview, setImagePrintPreview] = useState('');
  const [imagePrintSubmitting, setImagePrintSubmitting] = useState(false);
  const [remoteReceiptPrintLoading, setRemoteReceiptPrintLoading] = useState({});
  const [kooulDecisionLoading, setKooulDecisionLoading] = useState({});
  const [callReviewModalVisible, setCallReviewModalVisible] = useState(false);
  const [callReviewCustomerInfo, setCallReviewCustomerInfo] = useState({ name: '', phone: '' });
  const [callReviewForm, setCallReviewForm] = useState(() => createEmptyCallReviewForm());
  const [callReviewSubmitting, setCallReviewSubmitting] = useState(false);
  const [glovoSyncLoading, setGlovoSyncLoading] = useState(false);
  const [glovoSistersSyncLoading, setGlovoSistersSyncLoading] = useState(false);
  const [portalSyncLoading, setPortalSyncLoading] = useState(false);
  const [glovoLiveSyncErrors, setGlovoLiveSyncErrors] = useState([]);
  const [selectedStoreHasSisters, setSelectedStoreHasSisters] = useState(false);
  const [reservedReviewModalVisible, setReservedReviewModalVisible] = useState(false);
  const [reservedReviewOrder, setReservedReviewOrder] = useState(null);
  const [reservedMovementsForm, setReservedMovementsForm] = useState([]);
  const [productDetailsIndex, setProductDetailsIndex] = useState({});
  const [componentOptions, setComponentOptions] = useState([]);
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [productsIndex, setProductsIndex] = useState({});
  const [stockMovementsIndex, setStockMovementsIndex] = useState({});
  const [reservedReviewSubmitting, setReservedReviewSubmitting] = useState(false);
  const [reservedDetailsLoading, setReservedDetailsLoading] = useState(false);
  const [affectModalVisible, setAffectModalVisible] = useState(false);
  const [selectedOrderToAffect, setSelectedOrderToAffect] = useState(null);
  const [affectUserId, setAffectUserId] = useState('');
  const [affectStatus, setAffectStatus] = useState('ongoing');
  const [usersList, setUsersList] = useState([]);
  const [affectSubmitting, setAffectSubmitting] = useState(false);
  const [storeUsers, setStoreUsers] = useState({});
  const [acceptedViewMode, setAcceptedViewMode] = useState('cards'); // 'cards', 'kanban', or 'products'
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [newUserCardUserId, setNewUserCardUserId] = useState('');
  const [storeProcessors, setStoreProcessors] = useState([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [cardsPerRow, setCardsPerRow] = useState(6);

  const kooulPlateformsRef = useRef([]);
  const kooulSyncIntervalRef = useRef(null);
  const kooulPendingAudioRef = useRef(null);
  const componentOptionsLoadedRef = useRef(false);
  const productsIndexLoadedRef = useRef(false);
  const ordersRequestIdRef = useRef(0);

  const roles = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('roles') || '[]');
    } catch (error) {
      console.error('Unable to parse roles from sessionStorage.', error);
      return [];
    }
  }, []);

  const isAdminOrManager = roles.includes('admin') || roles.includes('manager');
  const isAdmin = roles.includes('admin');
  const isCarrefourUser = roles.includes('carrefour');
  const canViewCustomerInfo = isAdminOrManager || isCarrefourUser;
  const isOperatorCarrefour = roles.includes('carrefour') || roles.includes('operator') || roles.includes('driver');
  const selectedStoreType = useMemo(
    () => String(selectedStore?.storeType || selectedStore?.type || '').toLowerCase(),
    [selectedStore]
  );
  const isCarrefourStoreSelected = selectedStoreType === 'carrefour';
  const defaultStoreId = sessionStorage.getItem('default_store_id');
  const affectedStoreIds = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('affected_store_ids');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [];
    } catch (error) {
      return [];
    }
  }, []);
  const activeStoreOptions = useMemo(
    () => stores.filter((store) => store?.is_active !== false && store?.storeCode).map(toStoreOption),
    [stores]
  );
  const canManagerChangeStore = useMemo(() => {
    if (!roles.includes('manager')) return false;

    const linkedStoreIds = new Set(
      [defaultStoreId, ...affectedStoreIds]
        .map((id) => String(id || '').trim())
        .filter(Boolean)
    );

    return linkedStoreIds.size > 1 || activeStoreOptions.length > 1;
  }, [activeStoreOptions.length, affectedStoreIds, defaultStoreId, roles]);
  const allStoresOption = useMemo(
    () => ({ label: `Tous les magasins actifs (${activeStoreOptions.length})`, value: ALL_STORES_VALUE, isAll: true }),
    [activeStoreOptions.length]
  );
  const isAllStoresSelected = isAdmin && selectedStore?.value === ALL_STORES_VALUE;

  useEffect(() => {
    if (!isAdmin || activeStoreOptions.length === 0) return;

    setReportStoreColors((currentColors) => {
      const nextColors = { ...currentColors };
      const usedColors = new Set(Object.values(nextColors));
      let changed = false;

      activeStoreOptions.forEach((store) => {
        const storeKey = String(store?.storeId || store?.value || '').trim();
        if (!storeKey || nextColors[storeKey]) return;

        const availableColor = REPORT_STORE_COLORS.find((color) => !usedColors.has(color));
        const color = availableColor || createFallbackStoreColor(storeKey);
        nextColors[storeKey] = color;
        usedColors.add(color);
        changed = true;
      });

      if (!changed) return currentColors;
      saveReportStoreColors(nextColors);
      return nextColors;
    });
  }, [activeStoreOptions, isAdmin]);
  const availableReportDates = useMemo(() => {
    const dates = new Set();
    [...inProgressOrders, ...readyOrders, ...goneOrders].forEach((order) => {
      const dateKey = resolveOrderDateKey(order);
      if (dateKey) dates.add(dateKey);
    });

    return Array.from(dates)
      .sort((left, right) => right.localeCompare(left))
      .map((value) => ({
        value,
        label: new Date(`${value}T00:00:00`).toLocaleDateString('fr-FR'),
      }));
  }, [goneOrders, inProgressOrders, readyOrders]);

  useEffect(() => {
    if (availableReportDates.length === 0) return;
    const selectedDateExists = availableReportDates.some((option) => option.value === reportDate);
    if (!selectedDateExists) setReportDate(availableReportDates[0].value);
  }, [availableReportDates, reportDate]);

  const adminReport = useMemo(() => {
    const uniqueOrders = deduplicateOrders([...inProgressOrders, ...readyOrders, ...goneOrders])
      .filter((order) => resolveOrderDateKey(order) === reportDate);
    const grouped = new Map();

    uniqueOrders.forEach((order) => {
      const hour = resolveOrderHour(order);
      if (hour === null) return;
      const storeKey = String(order?.__storeId || order?.store_id || order?.__storeCode || 'unknown');
      const storeLabel = order?.__storeName || order?.__storeCode || 'Magasin non identifié';
      const current = grouped.get(storeKey) || {
        label: storeLabel,
        totalOrders: 0,
        totalAmount: 0,
        ordersByHour: Array(24).fill(0),
        amountByHour: Array(24).fill(0),
      };
      current.totalOrders += 1;
      current.totalAmount += computeOrderTotal(order);
      current.ordersByHour[hour] += 1;
      current.amountByHour[hour] += computeOrderTotal(order);
      grouped.set(storeKey, current);
    });

    const points = Array.from(grouped.entries())
      .sort(([, left], [, right]) => right.totalOrders - left.totalOrders || left.label.localeCompare(right.label, 'fr'))
      .map(([storeKey, value]) => ({ ...value, storeKey }));

    const datasets = points.flatMap((store) => {
      const color = reportStoreColors[store.storeKey] || createFallbackStoreColor(store.storeKey);
      return [
        {
          label: `${store.label} - Commandes`,
          data: store.ordersByHour,
          borderColor: color,
          backgroundColor: color,
          pointBackgroundColor: color,
          pointRadius: 3,
          pointHoverRadius: 6,
          tension: 0.3,
          fill: false,
          yAxisID: 'orders',
        },
        {
          label: `${store.label} - Montant`,
          data: store.amountByHour.map((amount) => Number(amount.toFixed(2))),
          borderColor: color,
          backgroundColor: color,
          pointBackgroundColor: color,
          pointStyle: 'rectRot',
          pointRadius: 3,
          pointHoverRadius: 6,
          borderDash: [7, 5],
          tension: 0.3,
          fill: false,
          yAxisID: 'amount',
        },
      ];
    });

    return {
      totalOrders: uniqueOrders.length,
      totalAmount: uniqueOrders.reduce((sum, order) => sum + computeOrderTotal(order), 0),
      data: {
        labels: Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}h`),
        datasets,
      },
    };
  }, [goneOrders, inProgressOrders, readyOrders, reportDate, reportStoreColors]);

  const adminReportOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top' },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => context.dataset.yAxisID === 'amount'
            ? `${context.dataset.label}: ${Number(context.parsed.y || 0).toLocaleString('fr-FR')} DHS`
            : `${context.dataset.label}: ${context.parsed.y}`,
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: 'Heure de la journée' },
        ticks: { autoSkip: true, maxTicksLimit: 12, maxRotation: 0 },
      },
      orders: {
        beginAtZero: true,
        position: 'left',
        title: { display: true, text: 'Commandes' },
        ticks: { precision: 0 },
      },
      amount: {
        beginAtZero: true,
        position: 'right',
        title: { display: true, text: 'Montant (DHS)' },
        grid: { drawOnChartArea: false },
        ticks: { callback: (value) => `${Number(value).toLocaleString('fr-FR')} DHS` },
      },
    },
  }), []);

  const ComponentOption = (props) => {
    const { data } = props;
    return (
      <selectComponents.Option {...props}>
        <div className="reserve-review-select-option">
          {data.imageUrl ? (
            <img className="reserve-review-select-image" src={data.imageUrl} alt={data.label} />
          ) : (
            <div className="reserve-review-select-placeholder" aria-hidden="true" />
          )}
          <span className="reserve-review-select-label">{data.label}</span>
        </div>
      </selectComponents.Option>
    );
  };

  useEffect(() => {
    let mounted = true;
    const checkSisters = async () => {
      if (!selectedStore || !selectedStore.storeId) {
        if (mounted) setSelectedStoreHasSisters(false);
        return;
      }

      try {
        const plateformsRes = await fetchPlateformsByStore(selectedStore.storeId);
        const plateforms = Array.isArray(plateformsRes) ? plateformsRes : plateformsRes?.response || plateformsRes?.data || [];
        const has = (plateforms || []).some((p) => {
          if (!p) return false;
          const type = `${p?.type || ''}`.toLowerCase();
          if (type !== 'glovo_sisters') return false;
          const ids = Array.isArray(p.store_ids) ? p.store_ids.map((x) => String(normalizeId(x))) : [];
          return ids.includes(String(selectedStore.storeId));
        });
        if (mounted) setSelectedStoreHasSisters(Boolean(has));
      } catch (err) {
        console.error('Error checking sisters plateforms for store:', err);
        if (mounted) setSelectedStoreHasSisters(false);
      }
    };

    checkSisters();
    return () => {
      mounted = false;
    };
  }, [selectedStore]);

  const ComponentSingleValue = (props) => {
    const { data } = props;
    return (
      <selectComponents.SingleValue {...props}>
        <div className="reserve-review-select-option">
          {data.imageUrl ? (
            <img className="reserve-review-select-image" src={data.imageUrl} alt={data.label} />
          ) : (
            <div className="reserve-review-select-placeholder" aria-hidden="true" />
          )}
          <span className="reserve-review-select-label">{data.label}</span>
        </div>
      </selectComponents.SingleValue>
    );
  };

  const clearKooulSyncInterval = useCallback(() => {
    if (kooulSyncIntervalRef.current) {
      clearInterval(kooulSyncIntervalRef.current);
      kooulSyncIntervalRef.current = null;
    }
  }, []);

  const triggerKooulOrdersSync = useCallback(async () => {
    const plateforms = kooulPlateformsRef.current;
    if (!Array.isArray(plateforms) || plateforms.length === 0) {
      return;
    }

    await Promise.all(
      plateforms.map(async (plateform) => {
        const plateformId = plateform?._id ?? plateform?.id ?? plateform?.plateform_id;
        if (!plateformId && !selectedStore) {
          return;
        }

        try {
          
          await syncKooulOrders({ plateform_id: plateformId });
        } catch (error) {
          console.error('syncKooulOrders error:', error);
        }
      })
    );
  }, []);

  const resetSelectedStore = () => {
    setSelectedStore(isAdmin ? allStoresOption : null);
    setInProgressOrders([]);
    setReadyOrders([]);
    setGoneOrders([]);
    setStoreProcessors([]);
  };

  const handleStoreSelection = (store) => {
    ordersRequestIdRef.current += 1;
    setInProgressOrders([]);
    setReadyOrders([]);
    setGoneOrders([]);

    if (store?.isAll) {
      setSelectedStore(store);
      setStoreProcessors([]);
      return;
    }

    const processors_store = stores.find(s => String(s._id) === String(store?.storeId) || String(s._id) === String(store?.id))?.processors || [];
    setSelectedStore(store);
    // Set processors from the selected store
    if (processors_store && Array.isArray(processors_store)) {
      setStoreProcessors(processors_store);
    } else {
      setStoreProcessors([]);
    }
  };

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const fetchStores = async () => {
    try {
      const response = await fetch(`${API}/distinctStoreIds`, header());
      const payload = await response.json();
      const availableStores = (Array.isArray(payload?.response) ? payload.response : [])
        .filter((store) => store?.is_active !== false && store?.storeCode);
      setStores(availableStores);
      if (isAdmin) {
        setSelectedStore({
          label: `Tous les magasins actifs (${availableStores.length})`,
          value: ALL_STORES_VALUE,
          isAll: true,
        });
        setStoreProcessors([]);
        return;
      }

      if (defaultStoreId) {
        const defaultStore = availableStores.find((store) => String(store._id) === String(defaultStoreId));
        if (defaultStore) {
          setSelectedStore(toStoreOption(defaultStore));
          // Set processors state immediately
          setStoreProcessors(defaultStore.processors || []);
          return;
        }
      }

      if (availableStores.length > 0) {
        const firstStore = availableStores[0];
        setSelectedStore(toStoreOption(firstStore));
        setStoreProcessors(firstStore.processors || []);
      }
    } catch (error) {
      console.error('fetchStores error:', error);
      toast.error('Impossible de charger les magasins.');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, header());
      const users = Array.isArray(response?.data) ? response.data : response?.data?.users || [];
      setUsersList(users);
    } catch (error) {
      console.error('fetchUsers error:', error);
      toast.error('Impossible de charger la liste des utilisateurs.');
    }
  };

  const fetchStoreUsers = async (storeId) => {
    if (!storeId) return;
    try {
      const response = await axios.get(`${API}/users/store/${storeId}`, header());
      const users = response?.data?.users || [];
      const usersMap = {};
      users.forEach((user) => {
        const fullName = [user.name, user.lastname].filter(Boolean).join(' ') || user.username || 'User';
        usersMap[user._id] = fullName;
      });
      setStoreUsers(usersMap);
    } catch (error) {
      console.error('fetchStoreUsers error:', error);
      setStoreUsers({});
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, header());
      const users = response?.data || [];
      const usersMap = {};
      users.forEach((user) => {
        const fullName = [user.name, user.lastname].filter(Boolean).join(' ') || user.username || 'User';
        usersMap[user._id] = fullName;
      });
      console.log('Fetched users map:', usersMap);
      setStoreUsers(prevUsers => ({ ...prevUsers, ...usersMap }));
    } catch (error) {
      console.error('fetchAllUsers error:', error);
    }
  };

  useEffect(() => {
    const audio = new Audio(kooulPendingAudioSrc);
    audio.loop = true;
    kooulPendingAudioRef.current = audio;

    return () => {
      audio.pause();
      audio.currentTime = 0;
      kooulPendingAudioRef.current = null;
    };
  }, []);

  // Auto-run sync user data on open: find first portal-enabled Glovo platform and call sync-user-data (limit 50)
  useEffect(() => {
    let mounted = true;
    const runAutoSync = async () => {
      try {
        setPortalSyncLoading(true);
        const plateformsRes = await fetchPlateforms();
        const plateforms = Array.isArray(plateformsRes)
          ? plateformsRes
          : plateformsRes?.response || plateformsRes?.data || [];

        const glovoPortal = (plateforms || []).find((p) => {
          if (!p) return false;
          const type = `${p?.type || ''}`.toLowerCase();
          return type === 'glovo' && p?.additionnal_data?.isportal === true;
        });

        if (!glovoPortal) {
          return;
        }

        const plateformId = glovoPortal._id || glovoPortal.id || glovoPortal.plaform_id;
        if (!plateformId) return;

        // fire the sync call (non-blocking); limit default 50
        await axios.post(`${API}/glovo-portal/sync-user-data`, { plateformId, limit: 50 }, header());
        toast.info('Sync utilisateur lancé (background)');
      } catch (err) {
        console.error('Auto sync user data error on OrdersGlovo open:', err);
      } finally {
        if (mounted) setPortalSyncLoading(false);
      }
    };

    runAutoSync();
    return () => {
      mounted = false;
    };
  }, []);

  const fetchCurrentOrders = async (url, status, options = {}) => {
    const { includeKooulPending = false } = options;
    try {
      const response = await fetch(url, header());
      const payload = await response.json();
      const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : '';
      const list = extractOrders(payload)
        .slice()
        .reverse()
        .filter((row) => {
          const productType = String(row.productType || '').toLowerCase();
          const orderStatus = String(row.orderStatus || '').toLowerCase();

          // Treat glovo and glovo_sisters the same way
          if (productType === 'glovo' || productType === 'glovo_sisters') {
            if (normalizedStatus === 'accepted' && ['pending', 'preorder'].includes(orderStatus)) return true;
            return normalizedStatus ? orderStatus === normalizedStatus : true;
          }

          if (productType === 'kooul') {
            if (!normalizedStatus) {
              return true;
            }

            const allowedStatuses = new Set([normalizedStatus]);
            if (includeKooulPending) {
              allowedStatuses.add('pending');
            }

            return allowedStatuses.has(orderStatus);
          }

          return false;
        });
      return list;
    } catch (error) {
      console.error('fetchCurrentOrders error:', error);
      return [];
    }
  };

  const fetchAllOrders = async () => {
    if (!selectedStore) {
      return;
    }

    const requestId = ++ordersRequestIdRef.current;
    const targetStores = isAllStoresSelected ? activeStoreOptions : [selectedStore];
    const storeResults = await Promise.all(
      targetStores.filter((store) => store?.value && !store?.isAll).map(async (store) => {
        const [inProgress, ready, gone] = await Promise.all([
          fetchCurrentOrders(
            `${API}/allCurrentOrders/${encodeURIComponent(store.value)}`,
            'accepted',
            { includeKooulPending: true }
          ),
          fetchCurrentOrders(`${API}/getDeliveryWaitings/${encodeURIComponent(store.value)}`, 'ready'),
          fetchCurrentOrders(`${API}/allGoneOrders/${encodeURIComponent(store.value)}`, 'gone'),
        ]);

        return {
          inProgress: inProgress.map((order) => annotateOrderStore(order, store)),
          ready: ready.map((order) => annotateOrderStore(order, store)),
          gone: gone.map((order) => annotateOrderStore(order, store)),
        };
      })
    );

    const mergedInProgress = sortOrdersNewestFirst(deduplicateOrders(storeResults.flatMap((result) => result.inProgress)));
    const mergedReady = sortOrdersNewestFirst(deduplicateOrders(storeResults.flatMap((result) => result.ready)));
    const mergedGone = sortOrdersNewestFirst(deduplicateOrders(storeResults.flatMap((result) => result.gone)));

    if (requestId !== ordersRequestIdRef.current) return;

    setPreorders(mergedInProgress.filter((order) => order.orderStatus === 'preorder'));
    setInProgressOrders(mergedInProgress.filter((order) => order.orderStatus !== 'preorder'));
    setReadyOrders(mergedReady);
    setGoneOrders(mergedGone);
    await prefetchStockMovements([...mergedInProgress, ...mergedReady, ...mergedGone]);

    try {
      const res = await fetch(`${API}/getAllGlovoProgrammed`, header());
      const data = await res.json();
      setGlovoProgrammed(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('fetchAllGlovoProgrammed error:', error);
      setGlovoProgrammed([]);
    }
  };

  const handleGlovoPartnersSync = async () => {
    if (!selectedStore) {
      toast.warn('Veuillez sélectionner un magasin.');
      return;
    }

    const payload = {};
    if (selectedStore?.storeId) {
      payload.storeId = selectedStore.storeId;
    }
    if (selectedStore?.value) {
      payload.storeCode = selectedStore.value;
    }

    if (!payload.storeId && !payload.storeCode) {
      toast.error('Identifiants du magasin introuvables pour la synchronisation Glovo.');
      return;
    }

    setGlovoSyncLoading(true);

    try {
      const response = await axios.post(`${API}/glovo/orders/sync-partners`, payload, header());
      const data = response?.data || {};
      const inserted = Number(data?.inserted || 0);
      const processed = Number(data?.processed || 0);
      const storeCode = data?.storeCode || payload.storeCode || selectedStore?.value || '';
      const summaryParts = [];
      if (Number.isFinite(processed) && processed > 0) {
        summaryParts.push(`${processed} traité${processed > 1 ? 's' : ''}`);
      }
      if (Number.isFinite(inserted) && inserted > 0) {
        summaryParts.push(`${inserted} nouveau${inserted > 1 ? 'x' : ''}`);
      }

      const summary = summaryParts.length ? ` (${summaryParts.join(', ')})` : '';
      const storeSuffix = storeCode ? ` pour ${storeCode}` : '';
      toast.success(`Synchronisation Glovo terminée${summary}${storeSuffix}`);
      await fetchAllOrders();
    } catch (error) {
      console.error('handleGlovoPartnersSync error:', error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Erreur lors de la synchronisation des commandes Glovo.";
      toast.error(message);
    } finally {
      setGlovoSyncLoading(false);
    }
  };

    const handleGlovoSistersSync = async () => {
      if (!selectedStore) {
        toast.warn('Veuillez sélectionner un magasin.');
        return;
      }

      setGlovoSistersSyncLoading(true);

      try {
        const data = await syncGlovoStoreLive(selectedStore, 'glovo_sisters');
        if (!data.success) {
          await fetchAllOrders();
          throw new Error(data.errors.map(error => error.message).join(' ; '));
        }
        const inserted = Number(data?.inserted || 0);
        const processed = Number(data?.processed || 0);
        const storeCode = selectedStore?.value || '';
        const summaryParts = [];
        if (Number.isFinite(processed) && processed > 0) {
          summaryParts.push(`${processed} traité${processed > 1 ? 's' : ''}`);
        }
        if (Number.isFinite(inserted) && inserted > 0) {
          summaryParts.push(`${inserted} nouveau${inserted > 1 ? 'x' : ''}`);
        }

        const summary = summaryParts.length ? ` (${summaryParts.join(', ')})` : '';
        const storeSuffix = storeCode ? ` pour ${storeCode}` : '';
        toast.success(`Synchronisation Glovo Sisters terminée${summary}${storeSuffix}`);
        await fetchAllOrders();
      } catch (error) {
        console.error('handleGlovoSistersSync error:', error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Erreur lors de la synchronisation des commandes Glovo Sisters.";
        toast.error(message);
      } finally {
        setGlovoSistersSyncLoading(false);
      }
    };

  const hasKooulPendingOrders = useMemo(() => {
    const listsToInspect = [inProgressOrders, readyOrders, goneOrders];
    return listsToInspect.some((list) =>
      (Array.isArray(list) ? list : []).some((order) => {
        const productType = String(order?.productType || '').toLowerCase();
        const orderStatus = String(order?.orderStatus || '').toLowerCase();
        return productType === 'kooul' && orderStatus === 'pending';
      })
    );
  }, [goneOrders, inProgressOrders, readyOrders]);

  useEffect(() => {
    const audio = kooulPendingAudioRef.current;
    if (!audio) {
      return;
    }

    if (hasKooulPendingOrders) {
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((error) => {
          console.error('Unable to play Kooul pending notification:', error);
        });
      }
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [hasKooulPendingOrders]);

  const resolveKooulOrderKey = (order) => {
    const orderCode = order?.orderCode;
    if (orderCode) {
      return String(orderCode);
    }

    const jobId = order?.customer_data?.job_id ?? order?.job_id ?? order?.orderNumber;
    if (jobId) {
      return String(jobId);
    }

    return String(order?._id ?? '');
  };

  const handleRefreshPortal = async () => {
    if (!selectedStore) {
      toast.warn('Veuillez sélectionner un magasin.');
      return;
    }

    setPortalSyncLoading(true);

    try {
      // Try to find a glovo platform marked as portal for this store
      const plateformsPayload = await fetchPlateformsByStore(selectedStore.storeId);
      const plateforms = extractPlateforms(plateformsPayload);
      const portalPlateform = (plateforms || []).find(p => (`${p?.type || ''}`).toLowerCase() === 'glovo' && p?.additionnal_data?.isportal === true);

      // Determine vendorId: prefer mapping from platform.additional_data.stores_maped
      let vendorId = selectedStore.value;
      try {
        const mapping = portalPlateform?.additionnal_data?.stores_maped;
        if (mapping) {
          if (Array.isArray(mapping)) {
            const m = mapping.find((m) => {
              const mid = m?.store_id?._id || m?.store_id || m?.storeId || (m?.store_id && m.store_id.$oid);
              return String(mid) === String(selectedStore.storeId);
            });
            if (m) vendorId = m.remote_store_id || m.vendorId || m.vendor_id || vendorId;
          } else if (typeof mapping === 'object') {
            const mid = mapping.store_id?._id || mapping.store_id || mapping.storeId || (mapping.store_id && mapping.store_id.$oid);
            if (String(mid) === String(selectedStore.storeId)) {
              vendorId = mapping.remote_store_id || mapping.vendorId || mapping.vendor_id || vendorId;
            }
          }
        }
      } catch (err) {
        console.error('Error resolving vendorId from platform mapping:', err);
      }

      const payload = { vendorId };
      if (portalPlateform && (portalPlateform._id || portalPlateform.id)) {
        payload.plateformId = portalPlateform._id || portalPlateform.id;
      }

      const response = await axios.post(`${API}/glovo/live-order/sync`, { ...payload, storeId: selectedStore.storeId }, header());
      if (response.data?.success === false) throw new Error('Synchronisation partielle : certaines commandes n’ont pas pu être mises à jour.');
      const data = response?.data || {};
      const inserted = Number(data?.inserted || 0);
      const processed = Number(data?.processed || 0);
      const summaryParts = [];
      if (Number.isFinite(processed) && processed > 0) summaryParts.push(`${processed} traité${processed > 1 ? 's' : ''}`);
      if (Number.isFinite(inserted) && inserted > 0) summaryParts.push(`${inserted} nouveau${inserted > 1 ? 'x' : ''}`);
      const summary = summaryParts.length ? ` (${summaryParts.join(', ')})` : '';
      toast.success(`Synchronisation Portal terminée${summary}`);
      await fetchAllOrders();
    } catch (err) {
      console.error('handleRefreshPortal error:', err);
      const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Erreur lors de la synchronisation Portal.';
      toast.error(message);
    } finally {
      setPortalSyncLoading(false);
    }
  };

  const handleKooulDecision = async (order, decision) => {
    if (!order) {
      return;
    }

    const jobId = order?.customer_data?.job_id ?? order?.job_id ?? order?.orderNumber;
    const vendorId =
      order?.customer_data?.vendor_id ??
      order?.customer_data?.customer_id ??
      order?.customer_data?.marketplace_user_id ??
      order?.customer_data?.user_id ??
      order?.store_id;

    if (!jobId || !vendorId) {
      toast.error("Impossible de retrouver les identifiants Kooul pour cette commande.");
      return;
    }

    const payload = {
      plateform_id: order?.plateform_id,
      storeId: order?.store_id,
      job_id: String(jobId),
      vendor_id: String(vendorId),
      customer_id: String(vendorId),
      accept_reject: decision,
    };

    const decisionKey = resolveKooulOrderKey(order);

    setKooulDecisionLoading((prev) => ({ ...prev, [decisionKey]: decision }));

    try {
      await axios.post(`${API}/kooul/orders/accept-reject`, payload, header());
      toast.success(
        `Commande ${decision === 'accept' ? 'acceptée' : 'rejetée'} avec succès.`
      );
      await fetchAllOrders();
    } catch (error) {
      console.error('Kooul order decision error:', error);
      const message =
        error?.response?.data?.message ||
        'Erreur lors de la mise à jour de la commande Kooul.';
      toast.error(message);
    } finally {
      setKooulDecisionLoading((prev) => {
        const updated = { ...prev };
        delete updated[decisionKey];
        return updated;
      });
    }
  };

  const printInNewTab = (orderData) => {
    const payload = {
      ...(orderData || {}),
      store: orderData?.__storeName || selectedStore?.label || '',
    };
    localStorage.setItem('order', JSON.stringify(payload));
    window.open('/print-receipt', '_blank');
  };

  const resolveRemoteReceiptPrintKey = (order) =>
    String(order?.orderCode || normalizeId(order?._id) || order?.id || '').trim();

  const handleRemoteReceiptPrint = async (order) => {
    const orderCode = String(order?.orderCode || '').trim();
    const loadingKey = resolveRemoteReceiptPrintKey(order);

    if (!orderCode) {
      toast.error('Code commande introuvable.');
      return;
    }

    setRemoteReceiptPrintLoading((prev) => ({ ...prev, [loadingKey]: true }));

    try {
      const response = await axios.patch(
        `${API}/orders/${encodeURIComponent(orderCode)}`,
        { ticketprinted: false },
        header()
      );
      if (String(order?.productType || '').toLowerCase() === 'glovo_sisters'
        && (response.data?.result !== true || response.data?.order?.ticketprinted !== false)) {
        throw new Error("La demande d'impression n'a pas été confirmée par le serveur.");
      }
      toast.success('Impression à distance demandée.');
      await fetchAllOrders();
    } catch (error) {
      console.error('handleRemoteReceiptPrint error:', error);
      const message =
        error?.response?.data?.response ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Erreur lors de la demande d'impression à distance.";
      toast.error(message);
    } finally {
      setRemoteReceiptPrintLoading((prev) => {
        const updated = { ...prev };
        delete updated[loadingKey];
        return updated;
      });
    }
  };

  const endOrder = async (orderCode) => {
    try {
      const response = await axios.post(`${API}/toGoneGlovo`, { orderCode }, header());
      if (response.status === 200) {
        toast.success('Commande marquée comme prête.');
        fetchAllOrders();
      }
    } catch (error) {
      console.error('endOrder error:', error);
      toast.error("Échec lors du passage à 'Prêt'.");
    }
  };

  const cancelOrder = async (orderCode) => {
    if (!window.confirm(`Êtes-vous sûr d'annuler la commande Numéro : ${orderCode} ?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/toCancelGlovo`, { orderCode }, header());
      if (response.status === 200) {
        toast.success('Commande annulée.');
        fetchAllOrders();
      }
    } catch (error) {
      console.error('cancelOrder error:', error);
      toast.error("Échec lors de l'annulation.");
    }
  };

  const loadComponentOptions = useCallback(async () => {
    if (componentOptionsLoadedRef.current && componentOptions.length && productsIndexLoadedRef.current) {
      return { componentOptions, productMap: productsIndex, productDetails: productDetailsIndex };
    }

    setComponentsLoading(true);
    try {
      const response = await fetchInventoryProducts();
      const productsList = Array.isArray(response?.products) ? response.products : response;
      const productMap = {};
      const productDetails = {};

      const availableOptions = (Array.isArray(productsList) ? productsList : [])
        .filter((product) => product?.isComposant)
        .map((product) => {
          const productId = normalizeId(product?._id || product?.id || product?.product_id || '');
          const label = product?.name || product?.description || product?.ref || product?.dolibarr_ref || 'Produit';
          const rawImage =
            product?.url_product ||
            product?.urlImage ||
            product?.urlimage ||
            product?.url_image ||
            product?.imageUrl ||
            product?.image_url ||
            product?.image;
          const imageUrl = resolveProductImageUrl(rawImage || product?.pic || product?.photo);

          if (productId) {
            productMap[productId] = label;
            productDetails[productId] = { label, imageUrl };
          }

          return {
            value: productId,
            label,
            imageUrl,
          };
        })
        .filter((option) => option.value);

      if (Array.isArray(productsList)) {
        productsList.forEach((product) => {
          const productId = normalizeId(product?._id || product?.id || product?.product_id || '');
          const label = product?.name || product?.description || product?.ref || product?.dolibarr_ref || 'Produit';
          const rawImage =
            product?.url_product ||
            product?.urlImage ||
            product?.urlimage ||
            product?.url_image ||
            product?.imageUrl ||
            product?.image_url ||
            product?.image;
          const imageUrl = resolveProductImageUrl(rawImage || product?.pic || product?.photo);

          if (productId) {
            productMap[productId] = label;
            productDetails[productId] = { label, imageUrl };
          }
        });
      }

      setProductsIndex(productMap);
      setProductDetailsIndex((prev) => ({ ...prev, ...productDetails }));
      setComponentOptions(availableOptions);
      componentOptionsLoadedRef.current = true;
      productsIndexLoadedRef.current = true;
      return { componentOptions: availableOptions, productMap, productDetails };
    } catch (error) {
      console.error('loadComponentOptions error:', error);
      toast.error('Impossible de charger les composants disponibles.');
      return { componentOptions: [], productMap: productsIndex, productDetails: productDetailsIndex };
    } finally {
      setComponentsLoading(false);
    }
  }, [componentOptions, productDetailsIndex, productsIndex]);

  const fetchStockMovementDetails = useCallback(async (movementId) => {
    if (!movementId) {
      return null;
    }

    if (stockMovementsIndex[movementId]) {
      return stockMovementsIndex[movementId];
    }

    try {
      const response = await axios.get(`${API}/inventory/movements/${movementId}`, header());
      const movement = response?.data || response;
      const normalizedId = normalizeId(
        movement?._id || movement?.id || movement?.movement_id || movement?.stockmouvementId
      );
      if (normalizedId) {
        setStockMovementsIndex((prev) => ({ ...prev, [normalizedId]: movement }));
      }
      return movement;
    } catch (error) {
      console.error('fetchStockMovementDetails error:', error);
      return null;
    }
  }, [stockMovementsIndex]);

  const prefetchStockMovements = useCallback(
    async (orders = []) => {
      const existingIds = new Set(Object.keys(stockMovementsIndex));
      const idsToFetch = [];

      orders.forEach((order) => {
        const reserved = Array.isArray(order?.inventory?.reserved) ? order.inventory.reserved : [];
        reserved.forEach((movement) => {
          const normalized = normalizeId(
            movement?.stockmouvementId || movement?._id || movement?.id || movement?.movement_id || ''
          );
          if (normalized && !existingIds.has(normalized)) {
            existingIds.add(normalized);
            idsToFetch.push(normalized);
          }
        });
      });

      if (!idsToFetch.length) {
        return;
      }

      try {
        const response = await axios.post(
          `${API}/inventory/movements/by-ids`,
          { stockmouvementids: idsToFetch },
          header({ 'Content-Type': 'application/json' })
        );
        const movements = Array.isArray(response?.data) ? response.data : response?.data?.data || [];
        const movementMap = movements.reduce((acc, movement) => {
          const normalized = normalizeId(
            movement?.stockmouvementId || movement?._id || movement?.id || movement?.movement_id || ''
          );
          if (normalized) {
            acc[normalized] = movement;
          }
          return acc;
        }, {});

        if (Object.keys(movementMap).length) {
          setStockMovementsIndex((prev) => ({ ...prev, ...movementMap }));
        }
      } catch (error) {
        console.error('prefetchStockMovements error:', error);
      }
    },
    [stockMovementsIndex]
  );

  const openReservedReviewModal = async (order) => {
    const reservedEntries = Array.isArray(order?.inventory?.reserved) ? order.inventory.reserved : [];
    if (!reservedEntries.length) {
      toast.info('Aucune réservation à examiner pour cette commande.');
      return;
    }

    setReservedDetailsLoading(true);
    try {
      const { productMap = productsIndex, productDetails = productDetailsIndex } =
        (await loadComponentOptions()) || {};
      const resolvedProductsIndex = Object.keys(productMap || {}).length ? productMap : productsIndex;
      const resolvedProductDetails =
        Object.keys(productDetails || {}).length > 0 ? productDetails : productDetailsIndex;

      const movementsWithDetails = await Promise.all(
        reservedEntries.map(async (movement) => {
          const stockmouvementId = normalizeId(
            movement?.stockmouvementId || movement?._id || movement?.id || movement?.movement_id || ''
          );
          const quantity = Number(movement?.quantity) || Number(movement?.qty) || 0;
          const movementDetail = await fetchStockMovementDetails(stockmouvementId);
          const productId = normalizeId(
            movementDetail?.product_id || movementDetail?.productId || movement?.product_id || movement?.productId || ''
          );
          const productDetail = productId ? resolvedProductDetails[productId] : null;
          const productName =
            productDetail?.label ||
            (productId && resolvedProductsIndex[productId]) ||
            movementDetail?.product?.name ||
            movementDetail?.product_name ||
            '';
          const productImage = resolveProductImageUrl(
            movementDetail?.product?.url_product ||
              productDetail?.imageUrl ||
              movementDetail?.product?.urlImage ||
              movementDetail?.product?.urlimage ||
              movementDetail?.product?.imageUrl ||
              movementDetail?.product_image_url ||
              movement?.urlImage ||
              movement?.imageUrl
          );

          return {
            stockmouvementId,
            quantity,
            action: 'validate',
            replacements: [],
            productId,
            productName,
            productImage,
          };
        })
      );

      setReservedReviewOrder(order);
      setReservedMovementsForm(movementsWithDetails);
      setReservedReviewModalVisible(true);
    } finally {
      setReservedDetailsLoading(false);
    }
  };

  const closeReservedReviewModal = () => {
    setReservedReviewModalVisible(false);
    setReservedReviewOrder(null);
    setReservedMovementsForm([]);
  };

  const updateReservedMovement = (index, updates) => {
    setReservedMovementsForm((prev) =>
      prev.map((movement, movementIndex) =>
        movementIndex === index
          ? {
              ...movement,
              ...updates,
              replacements: updates?.action === 'validate' ? [] : movement.replacements,
            }
          : movement
      )
    );
  };

  const updateReplacementEntry = (movementIndex, replacementIndex, updates) => {
    setReservedMovementsForm((prev) =>
      prev.map((movement, idx) => {
        if (idx !== movementIndex) {
          return movement;
        }

        const replacements = Array.isArray(movement.replacements) ? movement.replacements.slice() : [];
        replacements[replacementIndex] = { ...replacements[replacementIndex], ...updates };
        return { ...movement, action: 'replace', replacements };
      })
    );
  };

  const addReplacementEntry = (movementIndex) => {
    setReservedMovementsForm((prev) =>
      prev.map((movement, idx) => {
        if (idx !== movementIndex) {
          return movement;
        }

        const replacements = Array.isArray(movement.replacements) ? movement.replacements.slice() : [];
        replacements.push({ product_id_is_composent: '', quantity: movement.quantity || 1 });
        return { ...movement, replacements, action: 'replace' };
      })
    );
  };

  const removeReplacementEntry = (movementIndex, replacementIndex) => {
    setReservedMovementsForm((prev) =>
      prev.map((movement, idx) => {
        if (idx !== movementIndex) {
          return movement;
        }

        const replacements = (movement.replacements || []).filter((_, index) => index !== replacementIndex);
        return { ...movement, replacements };
      })
    );
  };

  const submitReservedReview = async () => {
    const payloadMovements = reservedMovementsForm
      .map((movement) => {
        const stockmouvementId = movement?.stockmouvementId;
        const quantity = Number(movement?.quantity) || 0;
        if (!stockmouvementId || quantity <= 0) {
          return null;
        }

        const replacements = (movement.replacements || [])
          .map((replacement) => {
            const productId = replacement?.product_id_is_composent || replacement?.productId || replacement?.product;
            const replacementQuantity = Number(replacement?.quantity) || 0;
            if (!productId || replacementQuantity <= 0) {
              return null;
            }

            return {
              product_id_is_composent: String(productId),
              quantity: replacementQuantity,
            };
          })
          .filter(Boolean);

        if (movement.action === 'replace' || replacements.length) {
          if (!replacements.length) {
            return null;
          }

          return {
            stockmouvementId: String(stockmouvementId),
            quantity,
            isvalid: false,
            remplacement: replacements,
          };
        }

        return {
          stockmouvementId: String(stockmouvementId),
          quantity,
          isvalid: true,
        };
      })
      .filter(Boolean);

    if (!payloadMovements.length) {
      toast.warn('Veuillez vérifier les quantités et sélections avant de soumettre.');
      return;
    }

    setReservedReviewSubmitting(true);
    try {
      await axios.post(
        `${API}/inventory/movements/review-reserved`,
        { movements: payloadMovements },
        header({ 'Content-Type': 'application/json' })
      );
      toast.success('Stock réservé mis à jour avec succès.');
      closeReservedReviewModal();
      
      // Wait for modal animation to complete before fetching
      setTimeout(() => {
        fetchAllOrders();
      }, 300);
    } catch (error) {
      console.error('submitReservedReview error:', error);
      toast.error('Impossible de mettre à jour les réservations.');
    } finally {
      setReservedReviewSubmitting(false);
    }
  };

  const handleStatusModal = (orderNumber, orderCode) => {
    setPreviewOrderNumber(orderNumber);
    setPreviewOrderCode(orderCode);
    setStatusModalVisible(true);
  };

  const imagePrinterOptions = useMemo(() => (
    (remotePrinters || []).flatMap((remote) => (
      (remote.printers || []).map((printer) => ({
        ...printer,
        computer_name: remote.computer_name || remote.machine_uid || '',
        store_id: remote.store_id || ''
      }))
    ))
  ), [remotePrinters]);

  const selectedImagePrinter = imagePrinterOptions.find((printer) => printer.printer_uid === imagePrintPrinterUid);

  const fetchRemotePrinters = async () => {
    const response = await axios.get(`${API}/printing/admin/printers`, header());
    const printers = response.data?.printers || [];
    setRemotePrinters(printers);
    const flattened = printers.flatMap((remote) => (
      (remote.printers || []).map((printer) => ({
        ...printer,
        computer_name: remote.computer_name || remote.machine_uid || '',
        store_id: remote.store_id || ''
      }))
    ));
    const preferred = flattened.find((printer) => printer.is_default) || flattened[0];
    if (preferred?.printer_uid) {
      setImagePrintPrinterUid((current) => current || preferred.printer_uid);
    }
  };

  const handleShowMot = (order) => {
    setSelectedOrderForMot(order);
    setMotEditorValue(order?.mot || '');
    setMotPrintTab('mot');
    setImagePrintFile(null);
    setImagePrintPreview('');
    setImagePrintPaperName('');
    fetchRemotePrinters().catch((error) => {
      console.error('fetchRemotePrinters error:', error);
      toast.error(error?.response?.data?.error || 'Erreur lors du chargement des imprimantes.');
    });
  };

  const handleDragStart = (order) => {
    setDraggedOrder(order);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDropToUser = async (userId, e) => {
    e.preventDefault();
    if (!draggedOrder) return;

    try {
      await axios.patch(
        `${API}/orders/${draggedOrder._id || draggedOrder.orderCode}/affected`,
        {
          user_id: userId,
          status: 'ongoing'
        },
        header()
      );
      await fetchAllOrders();
      setDraggedOrder(null);
    } catch (error) {
      console.error('Error affecting order:', error);
      alert('Erreur lors de l\'affectation de la commande');
    }
  };

  const handleMarkReady = async (orderCode) => {
    try {
      await axios.post(
        `${API}/endOrder`,
        { orderCode },
        header()
      );
      await fetchAllOrders();
    } catch (error) {
      console.error('Error marking order ready:', error);
      alert('Erreur lors du passage de la commande en prêt');
    }
  };

  useEffect(() => {
    setMotEditorValue(selectedOrderForMot?.mot || '');
  }, [selectedOrderForMot]);

  const hasMotContent = (value) => {
    if (!value) {
      return false;
    }

    const textOnly = value.replace(/<(.|\n)*?>/g, '').trim();
    return Boolean(textOnly);
  };

  const handleStatusChange = async (url) => {
    try {
      const res = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({ orderCode: previewOrderCode }),
        ...header({ 'Content-Type': 'application/json' }),
      });

      if (res.status === 200) {
        // Close modal first to avoid conflicts with re-render
        setStatusModalVisible(false);
        
        // Wait for modal animation to complete before fetching new data
        setTimeout(async () => {
          await fetchAllOrders();
        }, 300);
      } else {
        toast.error('Erreur lors de la mise à jour du statut.');
      }
    } catch (error) {
      console.error('handleStatusChange error:', error);
      toast.error('Erreur lors de la mise à jour du statut.');
    }
  };

  const handleBulkMarkAsGone = async () => {
    const filteredOrders = readyOrders.filter((order) => 
      String(order.orderNumber || '').includes(orderFilterReady)
    );

    if (filteredOrders.length === 0) {
      toast.warning('Aucune commande prête à marquer comme livrée.');
      return;
    }

    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir marquer ${filteredOrders.length} commande(s) comme livrée(s) ?`
    );

    if (!confirmed) return;

    let successCount = 0;
    let errorCount = 0;

    toast.info(`Traitement de ${filteredOrders.length} commande(s)...`);

    for (const order of filteredOrders) {
      try {
        const res = await fetch(`${API}/toGoneGlovo`, {
          method: 'POST',
          body: JSON.stringify({ orderCode: order.orderCode }),
          ...header({ 'Content-Type': 'application/json' }),
        });

        if (res.status === 200) {
          successCount++;
        } else {
          errorCount++;
          console.error(`Erreur pour la commande ${order.orderNumber}`);
        }
      } catch (error) {
        errorCount++;
        console.error(`Erreur pour la commande ${order.orderNumber}:`, error);
      }
    }

    // Refresh orders after bulk operation
    await fetchAllOrders();

    if (successCount > 0) {
      toast.success(`${successCount} commande(s) marquée(s) comme livrée(s) avec succès.`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} commande(s) n'ont pas pu être mises à jour.`);
    }
  };

  const handlePrintMot = () => {
    const mot = motEditorValue;
    if (!hasMotContent(mot)) {
      return;
    }

    try { sessionStorage.setItem('glovoNoteHtml', mot); } catch (e) {}
    const url = `/glovo-note-print`;
    window.open(url, '_blank', 'width=400,height=500');
  };

  const handleImagePrintFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImagePrintFile(null);
      setImagePrintPreview('');
      return;
    }
    if (file.type && !['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Merci de choisir une image JPG ou PNG.');
      event.target.value = '';
      return;
    }
    setImagePrintFile(file);
    setImagePrintPreview(URL.createObjectURL(file));
  };

  const handlePrintImageQueue = async () => {
    if (!imagePrintFile || !imagePrintPrinterUid) {
      toast.error('Choisissez une image et une imprimante.');
      return;
    }

    const paper = (selectedImagePrinter?.paper_sizes || []).find((item) => item.name === imagePrintPaperName);
    setImagePrintSubmitting(true);
    try {
      const imageBase64 = await fileToBase64(imagePrintFile);
      await axios.post(`${API}/printing/admin/queue/image`, {
        printer_uid: imagePrintPrinterUid,
        image_base64: imageBase64,
        content_type: imagePrintFile.type || 'image/jpeg',
        quantity: 1,
        paper_size: paper || (imagePrintPaperName ? { name: imagePrintPaperName } : undefined),
        title: selectedOrderForMot?.orderNumber ? `Glovo #${selectedOrderForMot.orderNumber}` : 'Glovo image',
        metadata: {
          source: 'cmd_glovo_print_image',
          order_code: selectedOrderForMot?.orderCode || '',
          order_number: selectedOrderForMot?.orderNumber || '',
          file_name: imagePrintFile.name,
          created_from_front: true
        }
      }, header());
      toast.success('Image ajoutee a la printing queue.');
      setImagePrintFile(null);
      setImagePrintPreview('');
    } catch (error) {
      console.error('handlePrintImageQueue error:', error);
      toast.error(error?.response?.data?.error || 'Erreur lors de la creation du job image.');
    } finally {
      setImagePrintSubmitting(false);
    }
  };

  const showCustomerData = (order) => {
    const customerData = order?.customer_data;
    if (customerData) {
      setSelectedCustomerData(customerData);
      setSelectedCustomerOrderCode(
        order?.orderCode || order?.order_code || order?.orderNumber || order?.order_number || ''
      );
      setCustomerModalVisible(true);
    }
  };

  const closeCustomerModal = () => {
    setCustomerModalVisible(false);
    setSelectedCustomerData(null);
    setSelectedCustomerOrderCode('');
  };

  const handleOpenCallReviewForm = () => {
    if (!selectedCustomerData?.customer_phone) {
      return;
    }

    setCallReviewCustomerInfo({
      name: selectedCustomerData?.customer_name || '',
      phone: selectedCustomerData?.customer_phone || '',
    });
    const existingCallReview =
      selectedCustomerData?.call_in_review || selectedCustomerData?.call_review || {};
    setCallReviewForm({
      calling_date: toDatetimeLocalValue(existingCallReview?.calling_date),
      review: existingCallReview?.review || '',
      is_liked:
        typeof existingCallReview?.is_liked === 'boolean' ? String(existingCallReview.is_liked) : '',
    });
    setCustomerModalVisible(false);
    setCallReviewModalVisible(true);
  };

  const closeCallReviewModal = () => {
    setCallReviewModalVisible(false);
    setCallReviewCustomerInfo({ name: '', phone: '' });
    setCallReviewForm(createEmptyCallReviewForm());
    setSelectedCustomerData(null);
    setSelectedCustomerOrderCode('');
  };

  const handleCallReviewFieldChange = (field) => (event) => {
    const value = event.target.value;
    setCallReviewForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleCallReviewSubmit = async (event) => {
    event.preventDefault();
    if (!selectedCustomerOrderCode) {
      toast.error('Order code is missing.');
      return;
    }

    const normalizedIsLiked =
      callReviewForm.is_liked === '' ? null : callReviewForm.is_liked === 'true';
    const payload = {
      calling_date: callReviewForm.calling_date ? new Date(callReviewForm.calling_date).toISOString() : null,
      review: callReviewForm.review || '',
      is_liked: normalizedIsLiked,
    };

    const requestConfig = header({ 'Content-Type': 'application/json' });
    setCallReviewSubmitting(true);
    try {
      const response = await fetch(
        `${API}/orders/${encodeURIComponent(selectedCustomerOrderCode)}/customer-data/call-review`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
          ...requestConfig,
        }
      );

      if (!response.ok) {
        throw new Error(`Unable to save call review (status ${response.status}).`);
      }

      toast.success('Call review saved.');
      closeCallReviewModal();
      
      // Wait for modal animation to complete before fetching
      setTimeout(async () => {
        await fetchAllOrders();
      }, 300);
    } catch (error) {
      console.error('handleCallReviewSubmit error:', error);
      toast.error('Unable to save call review.');
    } finally {
      setCallReviewSubmitting(false);
    }
  };

  const openAffectModal = (order) => {
    setSelectedOrderToAffect(order);
    setAffectUserId(order?.affected?.user_id?._id || order?.affected?.user_id || '');
    setAffectStatus(order?.affected?.status || 'ongoing');
    setAffectModalVisible(true);
    if (!usersList.length) {
      fetchUsers();
    }
  };

  const closeAffectModal = () => {
    setAffectModalVisible(false);
    setSelectedOrderToAffect(null);
    setAffectUserId('');
    setAffectStatus('ongoing');
  };

  // Aggregate products by order date for products view
  const aggregateProductsByDate = () => {
    const aggregated = {};
    const allOrders = [...inProgressOrders, ...readyOrders, ...goneOrders, ...glovoProgrammed];
    
    allOrders.forEach(order => {
      const orderDate = order.orderDate || 'No Date';
      
      if (!aggregated[orderDate]) {
        aggregated[orderDate] = {};
      }
      
      // Process products
      (order.products || []).forEach(product => {
        const productKey = product.title || product.barcode || 'Unknown Product';
        
        if (!aggregated[orderDate][productKey]) {
          aggregated[orderDate][productKey] = {
            name: productKey,
            quantity: 0,
            image: product.urlImage || '',
            barcode: product.barcode || '—',
            orders: [],
            productType: order.productType || 'Unknown'
          };
        }
        
        aggregated[orderDate][productKey].quantity += parseInt(product.qty || product.quantity || 1, 10);
        aggregated[orderDate][productKey].orders.push({
          orderCode: order.orderCode,
          orderNumber: order.orderNumber,
          productType: order.productType
        });
      });
    });
    
    return aggregated;
  };

  const productsByDate = aggregateProductsByDate();

  const handleAffectSubmit = async () => {
    if (!selectedOrderToAffect) {
      return;
    }

    if (!affectUserId) {
      toast.warning('Veuillez sélectionner un utilisateur.');
      return;
    }

    const orderId = selectedOrderToAffect?._id || selectedOrderToAffect?.id;
    if (!orderId) {
      toast.error('ID de commande introuvable.');
      return;
    }

    setAffectSubmitting(true);
    try {
      await axios.patch(
        `${API}/orders/${orderId}/affected`,
        {
          user_id: affectUserId,
          status: affectStatus
        },
        header()
      );
      toast.success(`Commande affectée avec succès.`);
      closeAffectModal();
      
      // Wait for modal animation to complete before fetching
      setTimeout(async () => {
        await fetchAllOrders();
      }, 300);
    } catch (error) {
      console.error('handleAffectSubmit error:', error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Erreur lors de l'affectation de la commande.";
      toast.error(message);
    } finally {
      setAffectSubmitting(false);
    }
  };

  const requestDolibarrTicket = useCallback(
    async (referenceProducts) => {
      const payload = (Array.isArray(referenceProducts) ? referenceProducts : [])
        .map((reference) => ({
          product_id: reference?.product_id || reference?.productId || reference?.id || reference?.product,
          quantity: numberify(reference?.qty || reference?.quantity || reference?.qty_value),
          store_id: selectedStore?.storeId || defaultStoreId || 0,
        }))
        .filter((entry) => entry.product_id && entry.quantity > 0);

      if (!payload.length) {
        return null;
      }

      try {
        const baseConfig = header({ Accept: 'application/json, text/plain, */*' });
        const normalizedHeaders = { ...(baseConfig?.headers || {}) };

        if (normalizedHeaders.ENCRYKEY && !normalizedHeaders.encrykey) {
          normalizedHeaders.encrykey = normalizedHeaders.ENCRYKEY;
        }

        const requestConfig = {
          ...baseConfig,
          headers: normalizedHeaders,
          withCredentials: true,
        };

        const response = await axios.post(`${API}/dolibarr/ticket`, payload, requestConfig);
        const ticket = findTicketPayload(response?.data);

        return ticket;
      } catch (ticketError) {
        console.error('requestDolibarrTicket error:', ticketError);
        throw ticketError;
      }
    },
    [defaultStoreId, selectedStore]
  );

  const fetchDolibarrProductById = useCallback(async (productId) => {
    const normalizedId = String(productId || '').trim();

    if (!normalizedId) {
      return null;
    }

    try {
      const baseConfig = header({ Accept: 'application/json, text/plain, */*' });
      const normalizedHeaders = { ...(baseConfig?.headers || {}) };

      if (normalizedHeaders.ENCRYKEY && !normalizedHeaders.encrykey) {
        normalizedHeaders.encrykey = normalizedHeaders.ENCRYKEY;
      }

      const requestConfig = {
        ...baseConfig,
        headers: normalizedHeaders,
        withCredentials: true,
      };

      const response = await axios.get(`${API}/product/${normalizedId}`, requestConfig);
      return response?.data || null;
    } catch (error) {
      console.warn(
        'fetchDolibarrProductById warning:',
        normalizedId,
        error?.response?.data || error.message
      );
      return null;
    }
  }, []);

  const buildTicketFromReferenceProducts = useCallback(
    async (order, references) => {
      const normalizedReferences = (Array.isArray(references) ? references : [])
        .map((reference) => ({
          productId: String(
            reference?.product_id || reference?.productId || reference?.id || reference?.product || ''
          ).trim(),
          qty: numberify(reference?.qty || reference?.quantity || reference?.qty_value || 0),
        }))
        .filter((entry) => entry.productId && entry.qty > 0);

      if (!normalizedReferences.length) {
        return null;
      }

      const productEntries = await Promise.all(
        normalizedReferences.map(async (entry) => ({
          ...entry,
          product: await fetchDolibarrProductById(entry.productId),
        }))
      );

      const ticketItems = productEntries
        .map((entry) => {
          const { product, qty, productId } = entry;

          if (!product || qty <= 0) {
            return null;
          }

          const unitPrice = numberify(
            product?.regular_price ?? product?.price ?? product?.net_price ?? product?.total ?? 0
          );
          const label = product?.name || product?.dolibarr_ref || productId;
          const barcode = product?.barcode ? String(product.barcode) : '';
          const ref = product?.dolibarr_ref || productId;

          return {
            name: label,
            label,
            price: unitPrice,
            qty,
            barcode,
            ref,
          };
        })
        .filter(Boolean);

      if (!ticketItems.length) {
        return null;
      }

      const netTotal = numberify(order?.totalNet ?? order?.totalnet);
      let totalFromItems = ticketItems.reduce((sum, item) => sum + item.price * item.qty, 0);

      if (netTotal > 0) {
        const lastIndex = ticketItems.length - 1;
        const lastItem = ticketItems[lastIndex];

        if (totalFromItems <= 0 && lastItem?.qty > 0) {
          const fallbackPrice = Number((netTotal / lastItem.qty).toFixed(2));
          ticketItems[lastIndex] = {
            ...lastItem,
            price: fallbackPrice,
          };

          totalFromItems = ticketItems.reduce((sum, item) => sum + item.price * item.qty, 0);
        } else if (totalFromItems > 0 && Math.abs(netTotal - totalFromItems) > 0.01 && lastItem?.qty > 0) {
          const difference = netTotal - totalFromItems;
          const adjustedPrice = numberify(lastItem.price) + difference / lastItem.qty;

          ticketItems[lastIndex] = {
            ...lastItem,
            price: Number(adjustedPrice.toFixed(2)),
          };

          totalFromItems = ticketItems.reduce((sum, item) => sum + item.price * item.qty, 0);
        }
      }

      const finalTotal = netTotal > 0 ? netTotal : totalFromItems;

      return {
        items: ticketItems.map((item) => ({
          name: item.name,
          label: item.label,
          price: item.price,
          qty: item.qty,
          barcode: item.barcode,
          ref: item.ref,
        })),
        barcodes: ticketItems.map((item) => item.barcode).filter((barcode) => barcode),
        count: ticketItems.length,
        total: finalTotal,
        amount: finalTotal,
      };
    },
    [fetchDolibarrProductById]
  );

  const updateOrderInLists = useCallback(
    (updatedOrder) => {
      const updatedId = resolveOrderId(updatedOrder);
      if (!updatedId) {
        return;
      }

      const mergeOrder = (orders) =>
        orders.map((entry) => (resolveOrderId(entry) === updatedId ? { ...entry, ...updatedOrder } : entry));

      setInProgressOrders((orders) => mergeOrder(orders));
      setReadyOrders((orders) => mergeOrder(orders));
      setGoneOrders((orders) => mergeOrder(orders));
    },
    []
  );

  const canShowTicketDeCaisse = useCallback(
    (order) => {
      const totalNet = numberify(order?.totalNet ?? order?.totalnet);
      const totalPrice = numberify(order?.totalPrice);
      const computedTotal = totalPrice > 0 ? totalPrice : computeOrderTotal(order);

      return isCarrefourStoreSelected && (computedTotal > 0 || totalNet > 0);
    },
    [isCarrefourStoreSelected]
  );

  const handleTicketDeCaisse = async (order) => {
    if (!selectedStore) {
      toast.warn('Veuillez sélectionner un magasin.');
      return;
    }

    if (!isCarrefourStoreSelected) {
      toast.warn('Le ticket de caisse est disponible uniquement pour les magasins Carrefour.');
      return;
    }

    const orderId = resolveOrderId(order);
    if (!orderId) {
      toast.error('Identifiant de commande introuvable.');
      return;
    }

    const totalNet = numberify(order?.totalNet ?? order?.totalnet);
    const referenceProducts = Array.isArray(order?.reference_products) ? order.reference_products : [];

    try {
      let ticket = null;
      let updatedOrder = order;
      let references = referenceProducts;

      if (totalNet > 0 && referenceProducts.length > 0) {
        ticket = await buildTicketFromReferenceProducts(order, referenceProducts);

        if (!ticket) {
          ticket = await requestDolibarrTicket(referenceProducts);
        }
      } else {
        const requestConfig = { ...header(), params: { orderidraw: orderId } };
        const response = await axios.post(`${API}/dolibarr/order/${orderId}/generate`, {}, requestConfig);
        const serverOrder = response?.data?.order;
        const generatedTicket = findTicketPayload(response?.data?.ticket || response?.data);

        if (serverOrder) {
          updatedOrder = { ...order, ...serverOrder };
          updateOrderInLists(updatedOrder);
        }

        references = Array.isArray(updatedOrder?.reference_products)
          ? updatedOrder.reference_products
          : referenceProducts;

        ticket =
          generatedTicket ||
          (references.length && totalNet <= 0
            ? await requestDolibarrTicket(references)
            : null);
      }

      if (!ticket || !Array.isArray(ticket.items) || ticket.items.length === 0) {
        toast.error('Impossible de générer le ticket pour cette commande.');
        return;
      }

      const ticketItems = Array.isArray(ticket.items) ? ticket.items : [];
      const ticketLines = buildTicketLines(ticketItems);
      const ticketTotal =
        numberify(ticket?.total ?? ticket?.amount) ||
        ticketLines.reduce((sum, line) => sum + (Number.isFinite(line.price) ? line.price : 0), 0);

      const orderLines = ticketItems.map((item) => ({
        fk_product: item?.ref || 0,
        qty: numberify(item?.qty ?? item?.quantity ?? 0),
        product_barcode: item?.barcode,
        product_ref: item?.ref,
        id: item?.ref,
        subprice: numberify(item?.price).toFixed(2),
        tva_tx: '0.00',
        ref: item?.ref || item?.barcode,
        label: item?.name || item?.label || item?.barcode,
        desc: '',
        way: 'glovo',
      }));

      

      const orderDateTime = formatOrderTicketDateTime(
        updatedOrder?.created_at || updatedOrder?.createdAt || order?.created_at || order?.createdAt || new Date()
      );

      localStorage.setItem(
        'orderTicket',
        JSON.stringify({
          storeName: selectedStore?.label || 'Ticket de Caisse',
          lines: ticketLines,
          total: ticketTotal,
          orderDateTime,
        })
      );

      window.open('/order-ticket', '_blank');
    } catch (error) {
      console.error('handleTicketDeCaisse error:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la génération du ticket.';
      toast.error(message);
    }
  };

  useEffect(() => {
    fetchStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    clearKooulSyncInterval();
    kooulPlateformsRef.current = [];

    if (!selectedStore?.storeId) {
      return undefined;
    }

    let cancelled = false;

    const loadKooulPlateforms = async () => {
      try {
        const payload = await fetchPlateformsByStore(selectedStore.storeId);
        const plateforms = extractPlateforms(payload);
        const kooulPlateforms = plateforms.filter((plateform) => {
          const name = String(plateform?.name || plateform?.plateform_name || '').toLowerCase();
          const type = String(plateform?.type || '').toLowerCase();
          return type === 'kooul' || name.startsWith(KOOUL_PLATEFORM_PREFIX);
        });

        if (cancelled || kooulPlateforms.length === 0) {
          return;
        }

        kooulPlateformsRef.current = kooulPlateforms;
        await triggerKooulOrdersSync();

        if (cancelled) {
          return;
        }

        kooulSyncIntervalRef.current = setInterval(() => {
          triggerKooulOrdersSync().catch((error) => console.error('kooulSyncInterval error:', error));
        }, KOOUL_SYNC_INTERVAL);
      } catch (error) {
        console.error('loadKooulPlateforms error:', error);
      }
    };

    loadKooulPlateforms();

    return () => {
      cancelled = true;
      clearKooulSyncInterval();
      kooulPlateformsRef.current = [];
    };
  }, [selectedStore, clearKooulSyncInterval, triggerKooulOrdersSync]);

  useEffect(() => {
    if (!selectedStore) {
      return;
    }

    fetchAllOrders();
    fetchAllUsers();
    
    const interval = setInterval(fetchAllOrders, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [selectedStore]);

  // Auto-sync Glovo orders every 1 minute when a store is selected
  useEffect(() => {
    if (!selectedStore?.storeId && !isAllStoresSelected) {
      return;
    }

    let syncing = false;
    let stopped = false;
    const syncGlovoOrders = async () => {
      if (syncing || stopped) return;
      syncing = true;
      const errors = [];
      try {
        const targets = isAllStoresSelected ? activeStoreOptions : [selectedStore];
        for (const store of targets) {
          if (stopped || !store?.storeId || store.isAll) continue;
          if (/^\d+$/.test(String(store?.value || ''))) {
            try {
              const response = await axios.post(`${API}/glovo/live-order/sync`, {
                vendorId: String(store.value), storeId: store.storeId,
              }, header());
              if (response.data?.success === false) console.warn('Partial Glovo live sync', response.data.errors);
            } catch (error) {
              console.error('Glovo live sync failed for store', store.value, error);
            }
          }
          try {
            const result = await syncGlovoStoreLive(store, 'glovo_sisters', { allowMissing: true });
            if (!result.success) {
              console.warn('Partial Glovo Sisters live sync', result.errors);
              errors.push(...result.errors.map(error => `${store.label || store.value}: ${error.message}`));
            }
          } catch (error) {
            console.error('Glovo Sisters live sync failed for store', store.value, error);
            errors.push(`${store.label || store.value}: ${error.message}`);
          }
        }
        if (!stopped) await fetchAllOrders();
      } catch (error) {
        console.error('Auto-sync Glovo orders error:', error);
      } finally {
        syncing = false;
        if (!stopped) setGlovoLiveSyncErrors(errors);
      }
    };

    // Sync immediately on mount
    syncGlovoOrders();

    // Then sync every 1 minute
    const syncInterval = setInterval(syncGlovoOrders, 60000); // 1 minute

    return () => { stopped = true; clearInterval(syncInterval); };
  }, [selectedStore, isAllStoresSelected, activeStoreOptions]);

  // Fetch users when kanban view is activated
  useEffect(() => {
    if (acceptedViewMode === 'kanban' && !usersList.length) {
      fetchUsers();
    }
  }, [acceptedViewMode, usersList.length]);

  // Clean up modal backdrop and body classes
  useEffect(() => {
    const anyModalVisible = statusModalVisible || customerModalVisible || reservedReviewModalVisible || callReviewModalVisible || affectModalVisible;
    
    if (anyModalVisible) {
      document.body.classList.add('modal-open');
    } else {
      // Clean up body classes and styles
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    // Cleanup on unmount only
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [statusModalVisible, customerModalVisible, reservedReviewModalVisible, callReviewModalVisible, affectModalVisible]);

  const renderReceiptPrintDropdown = (order, { compact = false } = {}) => {
    const loadingKey = resolveRemoteReceiptPrintKey(order);
    const isRemoteLoading = Boolean(remoteReceiptPrintLoading[loadingKey]);

    return (
      <div className={compact ? 'btn-group flex-fill' : 'btn-group w-100'}>
        <button
          type="button"
          className="btn btn-warning btn-sm dropdown-toggle w-100"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          title="Reçu"
        >
          <i className={`fas fa-receipt${compact ? '' : ' me-1'}`}></i>
          {!compact && 'Reçu'}
        </button>
        <ul className="dropdown-menu">
          <li>
            <button type="button" className="dropdown-item" onClick={() => printInNewTab(order)}>
              <i className="fas fa-print me-2 text-warning"></i>
              Imprimer sur place
            </button>
          </li>
          <li>
            <button
              type="button"
              className="dropdown-item"
              disabled={isRemoteLoading}
              onClick={() => handleRemoteReceiptPrint(order)}
            >
              <i className="fas fa-cloud-upload-alt me-2 text-primary"></i>
              {isRemoteLoading ? 'Envoi...' : 'Imprimer à distance'}
            </button>
          </li>
        </ul>
      </div>
    );
  };

  const renderOrderStoreBadge = (order) => {
    if (!isAdmin || !order?.__storeName) return null;
    return (
      <span className="badge bg-light text-dark border ms-1" title={order.__storeCode || order.__storeName}>
        <i className="fas fa-store me-1"></i>
        {order.__storeName}
      </span>
    );
  };

  return (
    <div className="container-fluid mt-4">
      {glovoLiveSyncErrors.length > 0 && (
        <div className="alert alert-warning" role="alert">
          <strong>Synchronisation Glovo Sisters incomplète : </strong>
          {glovoLiveSyncErrors.join(' ; ')}
        </div>
      )}
      <div className="mb-5">
        <div className="row">
          <div className="col-12 col-md-6">
            <img
              src={`${process.env?.REACT_APP_DATA_PIC_PATH}/uploads/glovo-seeklogo.png`}
              className="m-2"
              width={150}
              alt="Glovo"
            />
          </div>
          <div className="col-12 col-md-6 text-end">
            {!isOperatorCarrefour && !isAdmin && (
              <button className="btn btn-warning mb-3 mx-1" onClick={resetSelectedStore}>
                <i className="fa-solid fa-arrows-rotate" /> Change Store
              </button>
            )}
            <button className="btn btn-success mb-3 mx-1" onClick={fetchAllOrders} disabled={!selectedStore}>
              <i className="fa-solid fa-arrows-rotate" /> Recharger
            </button>
            <button
              className="btn btn-outline-primary mb-3 mx-1"
              onClick={handleGlovoPartnersSync}
              disabled={!selectedStore || isAllStoresSelected || glovoSyncLoading}
            >
              {glovoSyncLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Sync Glovo...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-cloud-arrow-down" /> Refresh Glovo
                </>
              )}
            </button>
            <button
              className="btn btn-outline-info mb-3 mx-1"
              onClick={handleRefreshPortal}
              disabled={!selectedStore || isAllStoresSelected || portalSyncLoading}
            >
              {portalSyncLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Sync Portal...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-cloud-arrow-down" /> Refresh Portal
                </>
              )}
            </button>
            {selectedStoreHasSisters && (
              <button
                className="btn btn-outline-secondary mb-3 mx-1"
                onClick={handleGlovoSistersSync}
                disabled={!selectedStore || isAllStoresSelected || glovoSistersSyncLoading}
              >
                {glovoSistersSyncLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Sync Sisters...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-cloud-arrow-down" /> Refresh Sisters
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {stores && stores.length > 0 ? (
          <Select
            placeholder="Sélectionner le magasin"
            options={isAdmin ? [allStoresOption, ...activeStoreOptions] : activeStoreOptions}
            value={selectedStore}
            onChange={handleStoreSelection}
            isDisabled={!isAdmin && !(roles.includes('manager') && canManagerChangeStore) && Boolean(selectedStore)}
          />
        ) : (
          <p>Aucun magasin disponible</p>
        )}
      </div>

      {/* Statistics Cards */}
      {selectedStore && (
        <div className="row mb-4 mt-4">
          <div className="col-md-4 mb-3">
            <div className="card shadow-sm border-0 h-100 bg-warning">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="text-white mb-1 opacity-75">En cours</h6>
                    <h2 className="mb-0 fw-bold text-white">{inProgressOrders.length}</h2>
                  </div>
                  <div className="bg-primary rounded-circle p-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="white" className="bi bi-clock" viewBox="0 0 16 16">
                      <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
                      <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4 mb-3">
            <div className="card shadow-sm border-0 h-100 bg-success">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="text-white mb-1 opacity-75">Prêt</h6>
                    <h2 className="mb-0 fw-bold text-white">{readyOrders.length}</h2>
                  </div>
                  <div className="bg-warning rounded-circle p-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="white" className="bi bi-check-circle" viewBox="0 0 16 16">
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                      <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4 mb-3">
            <div className="card shadow-sm border-0 h-100 bg-dark">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="text-white mb-1 opacity-75">Livrée</h6>
                    <h2 className="mb-0 fw-bold text-white">{goneOrders.length}</h2>
                  </div>
                  <div className="bg-info rounded-circle p-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="white" className="bi bi-truck" viewBox="0 0 16 16">
                      <path d="M0 3.5A1.5 1.5 0 0 1 1.5 2h9A1.5 1.5 0 0 1 12 3.5V5h1.02a1.5 1.5 0 0 1 1.17.563l1.481 1.85a1.5 1.5 0 0 1 .329.938V10.5a1.5 1.5 0 0 1-1.5 1.5H14a2 2 0 1 1-4 0H5a2 2 0 1 1-3.998-.085A1.5 1.5 0 0 1 0 10.5v-7zm1.294 7.456A1.999 1.999 0 0 1 4.732 11h5.536a2.01 2.01 0 0 1 .732-.732V3.5a.5.5 0 0 0-.5-.5h-9a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 .294.456zM12 10a2 2 0 0 1 1.732 1h.768a.5.5 0 0 0 .5-.5V8.35a.5.5 0 0 0-.11-.312l-1.48-1.85A.5.5 0 0 0 13.02 6H12v4zm-9 1a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm9 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAdmin && selectedStore && (
        <div className="card border-0 shadow-sm mb-4 overflow-hidden">
          <div className="card-body p-3 p-md-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
              <div>
                <div className="text-uppercase text-muted small fw-bold">Rapport Glovo</div>
                <h5 className="mb-0">Commandes et chiffre d'affaires par magasin et par heure</h5>
              </div>
              <div className="d-flex gap-2 flex-wrap align-items-end">
                <div>
                  <label htmlFor="glovo-report-date" className="form-label small text-muted fw-bold mb-1">
                    Date du rapport
                  </label>
                  <select
                    id="glovo-report-date"
                    className="form-control form-control-sm"
                    value={availableReportDates.some((option) => option.value === reportDate) ? reportDate : ''}
                    onChange={(event) => setReportDate(event.target.value)}
                    disabled={availableReportDates.length === 0}
                  >
                    {availableReportDates.length === 0 ? (
                      <option value="">Aucune date disponible</option>
                    ) : availableReportDates.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <span className="badge rounded-pill bg-danger px-3 py-2">
                  {adminReport.totalOrders} commande{adminReport.totalOrders === 1 ? '' : 's'}
                </span>
                <span className="badge rounded-pill bg-success px-3 py-2">
                  {adminReport.totalAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DHS
                </span>
              </div>
            </div>
            {adminReport.totalOrders > 0 ? (
              <div style={{ height: '320px' }}>
                <Line data={adminReport.data} options={adminReportOptions} />
              </div>
            ) : (
              <div className="text-center text-muted py-5">Aucune commande à afficher pour cette sélection.</div>
            )}
          </div>
        </div>
      )}

      {/* Accepted orders - View Toggle */}
      {preorders.length > 0 && (
        <section className="mt-4 mb-4">
          <h4>Précommandes <span className="badge bg-info text-dark">{preorders.length}</span></h4>
          <div className="row">
            {preorders.map((order) => (
              <div key={order._id || order.orderCode} className="col-12 col-md-6 col-xl-4 mb-3">
                <div className="card border-info h-100">
                  <div className="card-body">
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                      <strong>#{order.orderNumber || '—'}</strong>
                      <span className="badge bg-info text-dark">PREORDER</span>
                      {renderOrderStoreBadge(order)}
                    </div>
                    <div className="small text-muted">{order.orderCode}</div>
                    <div className="mt-2 fw-bold">
                      Prévue : {order.scheduled_at && Number.isFinite(Date.parse(order.scheduled_at))
                        ? new Date(order.scheduled_at).toLocaleString('fr-MA', { timeZone: 'Africa/Casablanca' })
                        : 'Horaire non communiqué'}
                    </div>
                    <ul className="my-2">
                      {(order.products || []).map((product, index) => (
                        <li key={index}>{product.quantity} × {product.title}
                          {(product.options || []).length > 0 && <div className="small text-muted">
                            {product.options.map((option) => `${option.quantity} × ${option.title}`).join(', ')}
                          </div>}
                        </li>
                      ))}
                    </ul>
                    {order.mot && <p className="small mb-2">{order.mot}</p>}
                    {order.pin && <div>PIN : <strong>{order.pin}</strong></div>}
                    {order.totalPrice != null && <div>{order.totalPrice} MAD</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      <div className="row mt-4 mb-3">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              Commandes en cours <span className="badge bg-warning text-dark">{inProgressOrders.length}</span>
            </h4>
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${acceptedViewMode === 'cards' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setAcceptedViewMode('cards')}
              >
                <i className="fas fa-th-large me-1"></i>
                Cartes
              </button>
              <button
                type="button"
                className={`btn btn-sm ${acceptedViewMode === 'kanban' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setAcceptedViewMode('kanban')}
              >
                <i className="fas fa-columns me-1"></i>
                Drag & Drop
              </button>
              <button
                type="button"
                className={`btn btn-sm ${acceptedViewMode === 'products' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setAcceptedViewMode('products')}
              >
                <i className="fas fa-box me-1"></i>
                Produits
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Accepted orders - Cards View */}
      {acceptedViewMode === 'cards' && (
        <div className="row">
          {inProgressOrders.map((order, index) => {
            const productType = String(order?.productType || '').toLowerCase();
            const orderStatus = String(order?.orderStatus || '').toLowerCase();
            const isKooulOrder = productType === 'kooul';
            const isKooulPending = isKooulOrder && orderStatus === 'pending';
            const kooulDecisionKey = resolveKooulOrderKey(order);
            const rowId = `accepted${index}`;
            const isExpanded = Boolean(expandedRows[rowId]);
            const userId = typeof order?.affected?.user_id === 'object' ? order.affected.user_id?._id : order.affected.user_id;

            return (
              <div key={order._id || order.orderCode || kooulDecisionKey} className="col-12 col-sm-6 col-md-4 col-xxl-2-4 mb-3">
                <div className="card h-100 shadow-sm border-0">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="d-flex align-items-center flex-wrap gap-1">
                        <span className="badge bg-warning text-dark" style={{ fontSize: 'calc(1rem + 8pt)' }}>#{order.orderNumber}</span>
                        {renderOrderStoreBadge(order)}
                      </div>
                      {order.mot && (
                        <span className="badge bg-success">
                          <i className="fas fa-comment me-1"></i>Mot
                        </span>
                      )}
                    </div>
                    <p className="text-muted mb-2 small">{order.orderCode}</p>
                    <p className="mb-1 small">
                      <i className="far fa-calendar me-2"></i>
                      {order.orderDate} {order.orderTime}
                    </p>
                    <p className="mb-2 small">
                      <span className="badge bg-info">{order.products?.length ?? order.productsCount ?? 0}</span> x Produits
                    </p>
                    {userId && (
                      <p className="mb-2 small">
                        <span className="badge bg-primary">
                          <i className="fas fa-user me-1"></i>
                          {(() => {
                            console.log('Looking up userId:', userId, 'in storeUsers:', storeUsers[userId]);
                            return storeUsers[userId] || 
                             (typeof order.affected.user_id === 'object'
                               ? [order.affected.user_id?.name, order.affected.user_id?.lastname].filter(Boolean).join(' ') || order.affected.user_id?.email || order.affected.user_id?.username
                               : userId) || 'Processing';
                          })()}
                        </span>
                        <span className={`badge ms-1 ${productType === 'glovo_sisters' ? 'bg-warning text-dark' : 'bg-info'}`}>
                          {productType.toUpperCase()}
                        </span>
                      </p>
                    )}

                    {isExpanded && order.products && (
                      <div className="row mt-3 mb-2">
                        {order.products.map((product, pIndex) => (
                          <div
                            className="col-6 text-center mb-2"
                            key={product._id || product.barcode || product.title || pIndex}
                          >
                            <img
                              src={resolveProductImageUrl(product.urlImage)}
                              width={80}
                              height={80}
                              style={{ objectFit: 'cover' }}
                              alt={product.title || 'Produit'}
                            />
                            <div className="small mt-1">{product.title}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="btn-group-vertical w-100 gap-1 mt-2">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleRow(rowId)}>
                        {isExpanded ? 'Masquer produits' : 'Voir produits'}
                      </button>

                      {isKooulPending ? (
                        <>
                          <button
                            className="btn btn-success btn-sm"
                            disabled={Boolean(kooulDecisionLoading[kooulDecisionKey])}
                            onClick={() => handleKooulDecision(order, 'accept')}
                          >
                            {kooulDecisionLoading[kooulDecisionKey] === 'accept' ? 'Acceptation...' : 'Accepter'}
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            disabled={Boolean(kooulDecisionLoading[kooulDecisionKey])}
                            onClick={() => handleKooulDecision(order, 'reject')}
                          >
                            {kooulDecisionLoading[kooulDecisionKey] === 'reject' ? 'Refus...' : 'Refuser'}
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-success btn-sm" onClick={() => handleMarkReady(order.orderCode)}>
                            <i className="fas fa-check me-1"></i>Marquer Prêt
                          </button>
                          {renderReceiptPrintDropdown(order)}
                          {canShowTicketDeCaisse(order) && (
                            <button className="btn btn-warning btn-sm" onClick={() => handleTicketDeCaisse(order)}>
                              Ticket de Caisse
                            </button>
                          )}
                          {hasReservedInventory(order) && (
                            <button className="btn btn-outline-danger btn-sm" onClick={() => openReservedReviewModal(order)}>
                              Stock réservé
                            </button>
                          )}
                          {order.mot && (
                            <button
                              className="btn btn-info btn-sm"
                              data-bs-toggle="modal"
                              data-bs-target="#showMot"
                              onClick={() => handleShowMot(order)}
                            >
                              <i className="fas fa-comment me-1"></i>Afficher le mot
                            </button>
                          )}
                          <button className="btn btn-danger btn-sm" onClick={() => cancelOrder(order.orderCode)}>
                            <i className="fas fa-times me-1"></i>Annuler
                          </button>
                          {isAdminOrManager && (
                            <button className="btn btn-primary btn-sm" onClick={() => openAffectModal(order)}>
                              <i className="fas fa-user-tag me-1"></i>Affecter
                            </button>
                          )}
                          {canViewCustomerInfo && order.customer_data && (
                            <button className="btn btn-info btn-sm" onClick={() => showCustomerData(order)}>
                              <i className="fas fa-user me-1"></i>Info Client
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Products View */}
      {acceptedViewMode === 'products' && (
        <div className="row mt-3">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Produits par Date de Commande</h5>
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center" style={{ minWidth: '200px' }}>
                  <label className="me-2 text-nowrap" style={{ fontSize: '0.875rem' }}>
                    <i className="fas fa-th me-1"></i>
                    {cardsPerRow} par ligne
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    min="3"
                    max="18"
                    value={cardsPerRow}
                    onChange={(e) => setCardsPerRow(Number(e.target.value))}
                    style={{ width: '120px' }}
                  />
                </div>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Rechercher un produit..."
                  value={productSearchQuery}
                  onChange={(e) => setProductSearchQuery(e.target.value)}
                  style={{ maxWidth: '300px' }}
                />
              </div>
            </div>
          </div>

          {Object.keys(productsByDate).sort((a, b) => {
            // Parse dd/mm/yyyy dates and sort descending (newest first)
            const parseDate = (dateStr) => {
              const [day, month, year] = dateStr.split('/');
              return new Date(year, month - 1, day);
            };
            return parseDate(b) - parseDate(a);
          }).map(dateKey => {
            const products = productsByDate[dateKey];
            const productList = Object.values(products)
              .filter(p => {
                if (!productSearchQuery) return true;
                const query = productSearchQuery.toLowerCase();
                return p.name.toLowerCase().includes(query) || 
                       (p.barcode && p.barcode.toLowerCase().includes(query));
              })
              .sort((a, b) => b.quantity - a.quantity);
            const totalProducts = productList.reduce((sum, p) => sum + p.quantity, 0);
            
            if (productList.length === 0) return null;
            
            return (
              <div key={dateKey} className="col-12 mb-4">
                <div className="card">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      <i className="fas fa-calendar-alt me-2"></i>
                      {dateKey}
                    </h6>
                    <span className="badge bg-primary">{totalProducts} articles total</span>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      {productList.map((product, idx) => {
                        // Calculate column class based on cardsPerRow
                        const getColClass = () => {
                          // For Bootstrap-friendly values, use Bootstrap grid
                          if (cardsPerRow === 12) return 'col-lg-1';
                          if (cardsPerRow === 6) return 'col-lg-2';
                          if (cardsPerRow === 4) return 'col-lg-3';
                          if (cardsPerRow === 3) return 'col-lg-4';
                          // For other values, we'll use custom width
                          return '';
                        };
                        const colClass = getColClass();
                        const customWidth = colClass ? {} : { flex: `0 0 ${100 / cardsPerRow}%`, maxWidth: `${100 / cardsPerRow}%` };
                        
                        return (
                        <div className={colClass || 'col-6 col-sm-4 col-md-3'} style={customWidth} key={`${dateKey}-${product.name}-${idx}`}>
                          <div className="card h-100 shadow-sm" style={{ transition: 'all 0.3s ease' }}>
                            {product.image && (
                              <div style={{ 
                                width: '100%', 
                                paddingTop: '100%',
                                position: 'relative',
                                backgroundColor: '#F9FAFB',
                                cursor: 'pointer',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px'
                                }}>
                                  <img 
                                    src={resolveProductImageUrl(product.image)} 
                                    alt={product.name}
                                    style={{ 
                                      maxWidth: '100%', 
                                      maxHeight: '100%', 
                                      objectFit: 'contain',
                                      display: 'block'
                                    }}
                                  />
                                </div>
                                <div style={{ 
                                  position: 'absolute', 
                                  top: '8px', 
                                  right: '8px', 
                                  backgroundColor: '#059669', 
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '56px',
                                  height: '56px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexDirection: 'column',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }}>
                                  <div style={{ fontWeight: 800, fontSize: '1.25rem', lineHeight: 1 }}>
                                    {product.quantity}
                                  </div>
                                  <div style={{ fontSize: '0.65rem', lineHeight: 1 }}>
                                    items
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            <div className="card-body p-2">
                              <h6 className="card-title" style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.25rem', lineHeight: 1.3 }}>
                                {product.name}
                              </h6>
                              <div className="d-flex gap-1 align-items-center mb-1">
                                <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>
                                  {product.productType}
                                </span>
                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                  {product.barcode}
                                </small>
                              </div>
                              
                              <div className="mt-2 pt-2" style={{ borderTop: '1px solid #E5E7EB' }}>
                                <div className="bg-light p-1 rounded" style={{ fontSize: '0.7rem' }}>
                                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                                    {product.orders.length} Commande{product.orders.length > 1 ? 's' : ''}
                                  </div>
                                  {product.orders.slice(0, 2).map((order, orderIdx) => (
                                    <div key={orderIdx} className="text-muted" style={{ fontSize: '0.65rem' }}>
                                      #{order.orderNumber || order.orderCode}
                                    </div>
                                  ))}
                                  {product.orders.length > 2 && (
                                    <div className="text-primary fst-italic" style={{ fontSize: '0.65rem' }}>
                                      +{product.orders.length - 2} de plus...
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {Object.keys(productsByDate).length === 0 && (
            <div className="col-12">
              <div className="alert alert-info text-center">
                Aucun produit à afficher
              </div>
            </div>
          )}
          {Object.keys(productsByDate).length > 0 && productSearchQuery && 
           Object.keys(productsByDate).every(dateKey => {
             const products = productsByDate[dateKey];
             return Object.values(products).filter(p => {
               const query = productSearchQuery.toLowerCase();
               return p.name.toLowerCase().includes(query) || 
                      (p.barcode && p.barcode.toLowerCase().includes(query));
             }).length === 0;
           }) && (
            <div className="col-12">
              <div className="alert alert-warning text-center">
                Aucun produit ne correspond à votre recherche "{productSearchQuery}"
              </div>
            </div>
          )}
        </div>
      )}

      {/* Accepted orders - Kanban Drag & Drop View */}
      {acceptedViewMode === 'kanban' && (
        <div className="row">
          <div className="col-12">
            <div style={{ overflowX: 'auto' }}>
              <div className="d-flex gap-3" style={{ minWidth: 'max-content' }}>
                {/* Unassigned Column */}
                <div
                  style={{ width: '300px', minHeight: '400px' }}
                  className="border rounded p-3 bg-light"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDropToUser(null, e)}
                >
                  <h6 className="text-center mb-3 fw-bold">
                    <i className="fas fa-inbox me-2"></i>
                    Non affecté
                    <span className="badge bg-secondary ms-2">
                      {inProgressOrders.filter(o => !o?.affected?.user_id).length}
                    </span>
                  </h6>
                  <div className="d-flex flex-column gap-2">
                    {inProgressOrders
                      .filter(order => !order?.affected?.user_id)
                      .map((order) => {
                        const productType = String(order?.productType || '').toLowerCase();
                        const orderStatus = String(order?.orderStatus || '').toLowerCase();
                        const isKooulOrder = productType === 'kooul';
                        const isKooulPending = isKooulOrder && orderStatus === 'pending';

                        return (
                          <div
                            key={order._id || order.orderCode}
                            className="card shadow-sm"
                            draggable={!isKooulPending}
                            onDragStart={() => handleDragStart(order)}
                            style={{ cursor: isKooulPending ? 'default' : 'grab' }}
                          >
                            <div className="card-body p-2">
                              <div className="d-flex justify-content-between mb-1">
                                <div>
                                  <span className="badge bg-warning text-dark">#{order.orderNumber}</span>
                                  {renderOrderStoreBadge(order)}
                                </div>
                                <div className="d-flex gap-1">
                                  <span className={`badge ${productType === 'glovo_sisters' ? 'bg-warning text-dark' : 'bg-info'}`}>
                                    {productType === 'glovo_sisters' ? 'Glovo Sis' : productType === 'glovo' ? 'Glovo' : productType.toUpperCase()}
                                  </span>
                                  {order.mot && <i className="fas fa-comment text-success"></i>}
                                </div>
                              </div>
                              <small className="text-muted d-block">{order.orderCode}</small>
                              <small className="d-block">{order.orderDate} {order.orderTime}</small>
                              <small>
                                <span className="badge bg-info">{order.products?.length ?? order.productsCount ?? 0}</span> produits
                              </small>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* User Columns */}
                {Object.entries(
                  inProgressOrders
                    .filter(order => order?.affected?.user_id)
                    .reduce((acc, order) => {
                      const userId = typeof order.affected.user_id === 'object' 
                        ? order.affected.user_id?._id 
                        : order.affected.user_id;
                      if (!acc[userId]) acc[userId] = [];
                      acc[userId].push(order);
                      return acc;
                    }, {})
                ).map(([userId, orders]) => {
                  const firstOrder = orders[0];
                  const userDisplayName = storeUsers[userId] || 
                    (typeof firstOrder?.affected?.user_id === 'object'
                      ? [firstOrder.affected.user_id?.name, firstOrder.affected.user_id?.lastname].filter(Boolean).join(' ') || firstOrder.affected.user_id?.email || firstOrder.affected.user_id?.username
                      : userId) || userId;

                  return (
                    <div
                      key={userId}
                      style={{ width: '300px', minHeight: '400px' }}
                      className="border rounded p-3 bg-light"
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDropToUser(userId, e)}
                    >
                      <h6 className="text-center mb-3 fw-bold">
                        <i className="fas fa-user me-2"></i>
                        {userDisplayName}
                        <span className="badge bg-primary ms-2">{orders.length}</span>
                      </h6>
                    <div className="d-flex flex-column gap-2">
                      {orders.map((order) => {
                        const productType = String(order?.productType || '').toLowerCase();
                        const orderStatus = String(order?.orderStatus || '').toLowerCase();
                        const isKooulOrder = productType === 'kooul';
                        const isKooulPending = isKooulOrder && orderStatus === 'pending';

                        return (
                          <div
                            key={order._id || order.orderCode}
                            className="card shadow-sm"
                            draggable={!isKooulPending}
                            onDragStart={() => handleDragStart(order)}
                            style={{ cursor: isKooulPending ? 'default' : 'grab' }}
                          >
                            <div className="card-body p-2">
                              <div className="d-flex justify-content-between mb-1">
                                <div>
                                  <span className="badge bg-warning text-dark">#{order.orderNumber}</span>
                                  {renderOrderStoreBadge(order)}
                                </div>
                                <div className="d-flex gap-1">
                                  <span className={`badge ${productType === 'glovo_sisters' ? 'bg-warning text-dark' : 'bg-info'}`}>
                                    {productType === 'glovo_sisters' ? 'Glovo Sis' : productType === 'glovo' ? 'Glovo' : productType.toUpperCase()}
                                  </span>
                                  {order.mot && <i className="fas fa-comment text-success"></i>}
                                </div>
                              </div>
                              <small className="text-muted d-block">{order.orderCode}</small>
                              <small className="d-block">{order.orderDate} {order.orderTime}</small>
                              <small>
                                <span className="badge bg-info">{order.products?.length ?? order.productsCount ?? 0}</span> produits
                              </small>
                              <div className="mt-2 d-flex gap-1 flex-wrap">
                                <button
                                  className="btn btn-success btn-sm flex-fill"
                                  onClick={() => handleMarkReady(order.orderCode)}
                                  title="Marquer Prêt"
                                >
                                  <i className="fas fa-check"></i>
                                </button>
                                {renderReceiptPrintDropdown(order, { compact: true })}
                                <button
                                  className="btn btn-danger btn-sm flex-fill"
                                  onClick={() => cancelOrder(order.orderCode)}
                                  title="Annuler"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                                {canViewCustomerInfo && order.customer_data && (
                                  <button
                                    className="btn btn-info btn-sm w-100"
                                    onClick={() => showCustomerData(order)}
                                    title="Info Client"
                                  >
                                    <i className="fas fa-user me-1"></i>Client
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}

                {/* Add New User Column */}
                <div
                  style={{ width: '300px', minHeight: '400px' }}
                  className="border border-2 border-dashed rounded p-3 bg-white"
                  onDragOver={newUserCardUserId ? handleDragOver : undefined}
                  onDrop={newUserCardUserId ? (e) => handleDropToUser(newUserCardUserId, e) : undefined}
                >
                  <h6 className="text-center mb-3 fw-bold text-muted">
                    <i className="fas fa-user-plus me-2"></i>
                    Affecter à un utilisateur
                  </h6>
                  
                  <div className="mb-3">
                    {(() => {
                      // Filter out users that are already processors
                      const processorUserIds = storeProcessors.map(p => {
                        const userId = p?.user_id;
                        if (typeof userId === 'object') {
                          return String(userId?.$oid || userId?._id || userId?.id || '');
                        }
                        return String(userId || '');
                      }).filter(Boolean);

                      console.log('Filtering users for new assignment. Store processors:', storeProcessors);
                      console.log('Extracted processor user IDs:', processorUserIds);
                      
                      const availableUsers = usersList.filter((user) => {
                        const currentUserId = String(user._id || user.id || '');
                        const isProcessor = processorUserIds.includes(currentUserId);
                        if (isProcessor) {
                          console.log(`Filtering out user ${user.name} ${user.lastname} (${currentUserId}) - is processor`);
                        }
                        return isProcessor;
                      });

                      console.log('Available users after filtering:', availableUsers.length, 'out of', usersList.length);

                      if (availableUsers.length === 0) {
                        return (
                          <div className="alert alert-warning small text-center mb-0">
                            <i className="fas fa-info-circle me-2"></i>
                            Tous les utilisateurs sont déjà des processeurs pour ce magasin
                          </div>
                        );
                      }

                      return (
                        <>
                          <select
                            className="form-select"
                            value={newUserCardUserId}
                            onChange={(e) => setNewUserCardUserId(e.target.value)}
                          >
                            <option value="">-- Sélectionner un utilisateur --</option>
                            {availableUsers.map((user) => (
                              <option key={user._id || user.id} value={user._id || user.id}>
                                {user.name || user.username || user.email} {user.lastname || ''}
                              </option>
                            ))}
                          </select>
                          {newUserCardUserId && (
                            <button 
                              className="btn btn-sm btn-outline-secondary mt-2 w-100"
                              onClick={() => setNewUserCardUserId('')}
                            >
                              <i className="fas fa-times me-1"></i>
                              Réinitialiser
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {newUserCardUserId ? (
                    <div className="alert alert-info small text-center mb-0">
                      <i className="fas fa-hand-pointer me-2"></i>
                      Glissez une commande ici pour l'affecter à <strong>{storeUsers[newUserCardUserId] || usersList.find(u => (u._id || u.id) === newUserCardUserId)?.name || 'cet utilisateur'}</strong>
                    </div>
                  ) : (
                    <div className="alert alert-secondary small text-center mb-0">
                      <i className="fas fa-info-circle me-2"></i>
                      Sélectionnez un utilisateur pour activer cette zone de dépôt
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ready & Gone tabs */}
      <ul className="nav nav-tabs mt-4" role="tablist">
        <li className="nav-item">
          <button className="nav-link active" id="readyTab" data-bs-toggle="tab" data-bs-target="#ready" type="button" role="tab">
            Ready <span className="badge bg-success ms-1">{readyOrders.length}</span>
          </button>
        </li>
        <li className="nav-item">
          <button className="nav-link" id="goneTab" data-bs-toggle="tab" data-bs-target="#gone" type="button" role="tab">
            Gone <span className="badge bg-dark ms-1">{goneOrders.length}</span>
          </button>
        </li>
      </ul>

      <div className="tab-content mt-3">
        <div className="tab-pane fade show active" id="ready" role="tabpanel" aria-labelledby="readyTab">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="flex-grow-1 me-2">
              <label htmlFor="orderFilterReady">Filter by Order Number:</label>
              <input
                type="text"
                id="orderFilterReady"
                className="form-control"
                value={orderFilterReady}
                onChange={(event) => setOrderFilterReady(event.target.value)}
              />
            </div>
            <div className="ms-2 align-self-end">
              <button
                type="button"
                className="btn btn-success btn-lg"
                onClick={handleBulkMarkAsGone}
                disabled={readyOrders.filter((order) => String(order.orderNumber || '').includes(orderFilterReady)).length === 0}
              >
                <i className="fas fa-check-double me-2"></i>
                Marquer tout comme livré
              </button>
            </div>
          </div>
          <div className="row">
            {readyOrders
              .filter((order) => String(order.orderNumber || '').includes(orderFilterReady))
              .map((order, index) => {
                const productType = String(order?.productType || '').toLowerCase();
                const rowId = `ready${index}`;
                const isExpanded = Boolean(expandedRows[rowId]);
                return (
                  <div key={rowId} className="col-12 col-sm-6 col-md-4 col-xxl-2-4 mb-3">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center flex-wrap gap-1">
                            <span className="badge bg-primary" style={{ fontSize: 'calc(1rem + 4pt)' }}>#{order.orderNumber}</span>
                            {renderOrderStoreBadge(order)}
                          </div>
                          {glovoProgrammed?.find((item) => item?._id === order.orderCode) && (
                            <span className="badge bg-success">Préparé</span>
                          )}
                        </div>
                        <p className="text-muted mb-2 small">{order.orderCode}</p>
                        <p className="mb-1 small">
                          <i className="far fa-calendar me-2"></i>
                          {order.orderDate} {order.orderTime}
                        </p>
                        <p className="mb-2 small">
                          <span className="badge bg-info text-dark">{order.productsCount}</span> x Produits
                        </p>
                        <p className="mb-2 fw-bold text-primary">{computeOrderTotal(order)} DHS</p>
                        {order?.affected?.user_id && (() => {
                          const userId = typeof order.affected.user_id === 'object' ? order.affected.user_id?._id : order.affected.user_id;
                          return (
                            <p className="mb-2 small">
                              <span className="badge bg-primary fs-6">
                                <i className="fas fa-user me-1"></i>
                                {storeUsers[userId] || 
                                 (typeof order.affected.user_id === 'object'
                                   ? [order.affected.user_id?.name, order.affected.user_id?.lastname].filter(Boolean).join(' ') || order.affected.user_id?.email || order.affected.user_id?.username
                                   : userId) || 'Affecté'}
                              </span>
                              <span className={`badge ms-1 ${productType === 'glovo_sisters' ? 'bg-warning text-dark' : 'bg-info'}`}>
                                {productType === 'glovo_sisters' ? 'Glovo Sis' : productType === 'glovo' ? 'Glovo' : productType.toUpperCase()}
                              </span>
                            </p>
                          );
                        })()}
                        <div className="btn-group-vertical w-100 gap-1">
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleRow(rowId)}>
                            {isExpanded ? 'Masquer détails' : 'Voir détails'}
                          </button>
                          {hasReservedInventory(order) && (
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => openReservedReviewModal(order)}
                            >
                              Stock réservé
                            </button>
                          )}
                          <button className="btn btn-info btn-sm" onClick={() => handleStatusModal(order.orderNumber, order.orderCode)}>
                            Changer statut
                          </button>
                          {canViewCustomerInfo && order.customer_data && (
                            <button className="btn btn-primary btn-sm" onClick={() => showCustomerData(order)}>
                              <i className="fas fa-user me-1"></i>Info Client
                            </button>
                          )}
                        </div>
                        {isExpanded && (
                          <div className="mt-3 border-top pt-3">
                            {(order.products || []).map((product, productIndex) => (
                              <div key={productIndex} className="mb-2 pb-2 border-bottom">
                                <div className="d-flex gap-2 align-items-center">
                                  <img
                                    src={resolveProductImageUrl(product.urlImage)}
                                    width="50"
                                    height="50"
                                    className="rounded"
                                    alt={product.title}
                                  />
                                  <div className="flex-grow-1 small">
                                    <div className="fw-bold">{product.title}</div>
                                    <div className="text-muted">Qté: {product.quantity}</div>
                                    {(product.options || []).map((option, optionIndex) => (
                                      <div key={optionIndex} className="text-muted small">
                                        {option.quantity} {option.title}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="tab-pane fade" id="gone" role="tabpanel" aria-labelledby="goneTab">
          <div className="mb-3">
            <label htmlFor="orderFilterGone">Filter by Order Number:</label>
            <input
              type="text"
              id="orderFilterGone"
              className="form-control"
              value={orderFilterGone}
              onChange={(event) => setOrderFilterGone(event.target.value)}
            />
          </div>
          <div className="row">
            {goneOrders
              .filter((order) => String(order.orderNumber || '').includes(orderFilterGone))
              .map((order, index) => {
                const productType = String(order?.productType || '').toLowerCase();
                const rowId = `gone${index}`;
                const isExpanded = Boolean(expandedRows[rowId]);
                return (
                  <div key={rowId} className="col-12 col-sm-6 col-md-4 col-xxl-2-4 mb-3">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center flex-wrap gap-1">
                            <span className="badge bg-dark" style={{ fontSize: 'calc(1rem + 4pt)' }}>#{order.orderNumber}</span>
                            {renderOrderStoreBadge(order)}
                          </div>
                          {glovoProgrammed?.find((item) => item?._id === order.orderCode) && (
                            <span className="badge bg-success">Préparé</span>
                          )}
                        </div>
                        <p className="text-muted mb-2 small">{order.orderCode}</p>
                        <p className="mb-1 small">
                          <i className="far fa-calendar me-2"></i>
                          {order.orderDate} {order.orderTime}
                        </p>
                        <p className="mb-2 small">
                          <span className="badge bg-info text-dark">{order.productsCount}</span> x Produits
                        </p>
                        <p className="mb-2 fw-bold text-primary">{computeOrderTotal(order)} DHS</p>
                        {order?.affected?.user_id && (() => {
                          const userId = typeof order.affected.user_id === 'object' ? order.affected.user_id?._id : order.affected.user_id;
                          return (
                            <p className="mb-2 small">
                              <span className="badge bg-primary">
                                <i className="fas fa-user me-1"></i>
                                {storeUsers[userId] || 
                                 (typeof order.affected.user_id === 'object'
                                   ? [order.affected.user_id?.name, order.affected.user_id?.lastname].filter(Boolean).join(' ') || order.affected.user_id?.email || order.affected.user_id?.username
                                   : userId) || 'Affecté'}
                              </span>
                              <span className={`badge ms-1 ${productType === 'glovo_sisters' ? 'bg-warning text-dark' : 'bg-info'}`}>
                                {productType === 'glovo_sisters' ? 'Glovo Sis' : productType === 'glovo' ? 'Glovo' : productType.toUpperCase()}
                              </span>
                            </p>
                          );
                        })()}
                        <div className="btn-group-vertical w-100 gap-1">
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleRow(rowId)}>
                            {isExpanded ? 'Masquer détails' : 'Voir détails'}
                          </button>
                          {hasReservedInventory(order) && (
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => openReservedReviewModal(order)}
                            >
                              Stock réservé
                            </button>
                          )}
                          <button className="btn btn-info btn-sm" onClick={() => handleStatusModal(order.orderNumber, order.orderCode)}>
                            Changer statut
                          </button>
                          {canViewCustomerInfo && order.customer_data && (
                            <button className="btn btn-primary btn-sm me-2" onClick={() => showCustomerData(order)}>
                              <i className="fas fa-user me-1"></i>Info Client
                            </button>
                          )}
                          {canShowTicketDeCaisse(order) && (
                            <button className="btn btn-warning btn-sm" onClick={() => handleTicketDeCaisse(order)}>
                              Ticket de Caisse
                            </button>
                          )}
                        </div>
                        {isExpanded && (
                          <div className="mt-3 border-top pt-3">
                            {(order.products || []).map((product, productIndex) => (
                              <div key={productIndex} className="mb-2 pb-2 border-bottom">
                                <div className="d-flex gap-2 align-items-center">
                                  <img
                                    src={resolveProductImageUrl(product.urlImage)}
                                    width="50"
                                    height="50"
                                    className="rounded"
                                    alt={product.title}
                                  />
                                  <div className="flex-grow-1 small">
                                    <div className="fw-bold">{product.title}</div>
                                    <div className="text-muted">Qté: {product.quantity}</div>
                                    {(product.options || []).map((option, optionIndex) => (
                                      <div key={optionIndex} className="text-muted small">
                                        {option.quantity} {option.title}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {reservedReviewModalVisible && reservedReviewOrder && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Révision du stock réservé</h5>
                <button type="button" className="close" onClick={closeReservedReviewModal} aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body reserve-review-scroll">
                <p className="mb-3">
                  Commande <strong>#{reservedReviewOrder.orderNumber}</strong> - Code{' '}
                  <strong>{reservedReviewOrder.orderCode}</strong>
                </p>
                <div className="reserve-review-products-scroll">
                  <div className="reserve-review-products">
                    {(reservedReviewOrder.products || []).map((product, index) => (
                      <div className="reserve-review-product-card" key={product.id || product._id || index}>
                        {product.urlImage ? (
                          <img
                            className="reserve-review-product-image"
                            src={resolveProductImageUrl(product.urlImage)}
                            alt={product.title || 'Produit'}
                          />
                        ) : (
                          <div className="reserve-review-product-placeholder">Pas d'image</div>
                        )}
                        <div className="reserve-review-product-name" title={product.title}>
                          {product.title || 'Produit sans nom'}
                        </div>
                        <div className="reserve-review-product-quantity">Qté : {product.quantity || 0}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-muted">
                  Ajustez la quantité vendue ou remplacez les réservations par des produits marqués « isComposant ».
                </p>
                {componentsLoading || reservedDetailsLoading ? (
                  <div className="d-flex align-items-center">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Chargement des composants...
                  </div>
                ) : (
                  <div className="reserve-review-list reserve-review-scroll">
                    {reservedMovementsForm.map((movement, movementIndex) => {
                      const selectedAction = movement?.action || 'validate';
                      return (
                        <div className="border rounded p-3 mb-3" key={movement.stockmouvementId || movementIndex}>
                          <div className="d-flex align-items-center reserve-review-product mb-3">
                            <div className="d-flex flex-column w-100">
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                <strong className="me-2">Réservation #{movementIndex + 1}</strong>
                                <span className="badge bg-light text-dark">ID : {movement.stockmouvementId}</span>
                              </div>
                              {movement.productName && (
                                <div className="reserve-review-product-info mt-2">
                                  {movement.productImage ? (
                                    <img
                                      className="reserve-review-product-thumb"
                                      src={movement.productImage}
                                      alt={movement.productName || 'Produit réservé'}
                                    />
                                  ) : (
                                    <div className="reserve-review-product-thumb reserve-review-product-thumb--placeholder">
                                      Pas d'image
                                    </div>
                                  )}
                                  <span className="badge bg-info text-dark align-self-start">
                                    Produit : {movement.productName}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="row g-3 align-items-center mb-3">
                            <div className="col-12 col-md-4">
                              <label className="form-label">Quantité</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="form-control"
                                value={movement.quantity ?? ''}
                                onChange={(event) =>
                                  updateReservedMovement(movementIndex, { quantity: event.target.value })
                                }
                              />
                            </div>
                            <div className="col-12 col-md-8">
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name={`action-${movementIndex}`}
                                  id={`validate-${movementIndex}`}
                                  checked={selectedAction === 'validate'}
                                  onChange={() => updateReservedMovement(movementIndex, { action: 'validate' })}
                                />
                                <label className="form-check-label" htmlFor={`validate-${movementIndex}`}>
                                  Valider la réservation
                                </label>
                              </div>
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name={`action-${movementIndex}`}
                                  id={`replace-${movementIndex}`}
                                  checked={selectedAction === 'replace'}
                                  onChange={() => updateReservedMovement(movementIndex, { action: 'replace' })}
                                />
                                <label className="form-check-label" htmlFor={`replace-${movementIndex}`}>
                                  Remplacer par un composant
                                </label>
                              </div>
                            </div>
                          </div>

                          {selectedAction === 'replace' && (
                            <div className="bg-light rounded p-3">
                              {(movement.replacements || []).map((replacement, replacementIndex) => (
                                <div className="row g-2 align-items-center mb-2" key={replacementIndex}>
                                  <div className="col-12 col-md-6">
                                    <label className="form-label">Produit composant</label>
                                    <Select
                                      options={componentOptions}
                                      value={componentOptions.find(
                                        (option) => option.value === String(replacement.product_id_is_composent || '')
                                      )}
                                      components={{ Option: ComponentOption, SingleValue: ComponentSingleValue }}
                                      onChange={(selected) =>
                                        updateReplacementEntry(movementIndex, replacementIndex, {
                                          product_id_is_composent: selected?.value || '',
                                        })
                                      }
                                      isClearable
                                      placeholder="Choisir un produit"
                                    />
                                  </div>
                                  <div className="col-12 col-md-3">
                                    <label className="form-label">Quantité</label>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      className="form-control"
                                      value={replacement.quantity ?? ''}
                                      onChange={(event) =>
                                        updateReplacementEntry(movementIndex, replacementIndex, {
                                          quantity: event.target.value,
                                        })
                                      }
                                    />
                                  </div>
                                  <div className="col-12 col-md-3">
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger mt-4"
                                      onClick={() => removeReplacementEntry(movementIndex, replacementIndex)}
                                    >
                                      Supprimer
                                    </button>
                                  </div>
                                </div>
                              ))}

                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => addReplacementEntry(movementIndex)}
                              >
                                Ajouter un remplacement
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeReservedReviewModal}>
                  Fermer
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={submitReservedReview}
                  disabled={reservedReviewSubmitting}
                >
                  {reservedReviewSubmitting ? 'Enregistrement...' : 'Valider les changements'}
                </button>
              </div>
            </div>
          </div>
          </div>
        </>
      )}

      <Modal
        modalId="showMot"
        title="Mot de la commande"
        submitLabel={motPrintTab === 'image' ? 'Envoyer l’image à la queue' : 'Imprimer'}
        onSubmit={motPrintTab === 'image' ? handlePrintImageQueue : handlePrintMot}
        submitDisabled={motPrintTab === 'image' ? (!imagePrintFile || !imagePrintPrinterUid || imagePrintSubmitting) : !hasMotContent(motEditorValue)}
      >
        <ul className="nav nav-tabs mb-3" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${motPrintTab === 'mot' ? 'active' : ''}`}
              type="button"
              onClick={() => setMotPrintTab('mot')}
            >
              <i className="fas fa-comment me-1"></i>
              Mot
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${motPrintTab === 'image' ? 'active' : ''}`}
              type="button"
              onClick={() => setMotPrintTab('image')}
            >
              <i className="fas fa-image me-1"></i>
              Image
            </button>
          </li>
        </ul>

        {motPrintTab === 'mot' ? (
          <ReactQuill value={motEditorValue} onChange={setMotEditorValue} theme="snow" />
        ) : (
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label fw-bold">Image à imprimer</label>
              <input
                className="form-control"
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleImagePrintFileChange}
              />
              <small className="text-muted">JPG ou PNG. L’image sera convertie en PDF puis envoyée à la printing queue.</small>
            </div>

            {imagePrintPreview && (
              <div className="col-12">
                <div className="border rounded p-2 bg-light d-inline-block">
                  <img src={imagePrintPreview} alt="Aperçu" style={{ maxWidth: 220, maxHeight: 180, objectFit: 'contain' }} />
                </div>
              </div>
            )}

            <div className="col-md-7">
              <label className="form-label fw-bold">Imprimante distante</label>
              <select
                className="form-select"
                value={imagePrintPrinterUid}
                onChange={(event) => {
                  setImagePrintPrinterUid(event.target.value);
                  setImagePrintPaperName('');
                }}
              >
                <option value="">Sélectionner une imprimante</option>
                {imagePrinterOptions.map((printer) => (
                  <option key={printer.printer_uid} value={printer.printer_uid}>
                    {printer.name} - {printer.computer_name || 'Poste'} {printer.is_default ? '(par défaut)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-5">
              <label className="form-label fw-bold">Format papier</label>
              <select
                className="form-select"
                value={imagePrintPaperName}
                onChange={(event) => setImagePrintPaperName(event.target.value)}
              >
                <option value="">Automatique</option>
                {(selectedImagePrinter?.paper_sizes || []).map((paper) => (
                  <option key={`${paper.name}-${paper.width}-${paper.height}`} value={paper.name}>
                    {paper.name || `${paper.width} x ${paper.height}`}
                  </option>
                ))}
              </select>
            </div>

            {imagePrintSubmitting && (
              <div className="col-12">
                <div className="alert alert-info mb-0">Envoi de l’image vers la queue...</div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {statusModalVisible && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Changer le statut</h5>
                <button type="button" className="close" onClick={() => setStatusModalVisible(false)} aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <h2>
                  Order Number : <span>{previewOrderNumber}</span>
                </h2>
                <h3>
                  Order Code : <span>{previewOrderCode}</span>
                </h3>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  onClick={() => handleStatusChange(`${API}/toAcceptedGlovo`)}
                >
                  Processing
                </button>
                <button
                  type="button"
                  className="btn btn-warning btn-lg"
                  onClick={() => handleStatusChange(`${API}/endOrder`)}
                >
                  Ready
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-lg"
                  onClick={() => handleStatusChange(`${API}/toGoneGlovo`)}
                >
                  Delivered
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setStatusModalVisible(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
          </div>
        </>
      )}

      {customerModalVisible && selectedCustomerData && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Customer Information</h5>
                <button type="button" className="close" onClick={closeCustomerModal} aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>
                  <strong>Name:</strong> {selectedCustomerData.customer_name || 'N/A'}
                </p>
                <p>
                  <strong>Phone:</strong>{' '}
                  {selectedCustomerData.customer_phone ? (
                    <a href={`tel:${selectedCustomerData.customer_phone}`}>
                      {selectedCustomerData.customer_phone}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </p>
                {isAdminOrManager && (
                  <>
                    <p>
                      <strong>Email:</strong> {selectedCustomerData.customer_email || 'N/A'}
                    </p>
                    <p>
                      <strong>Address:</strong> {selectedCustomerData.address || 'N/A'}
                    </p>
                    <p>
                      <strong>Payment Mode:</strong> {selectedCustomerData.payment_method || 'N/A'}
                    </p>
                  </>
                )}
                {isAdminOrManager &&
                  (selectedCustomerData?.call_in_review || selectedCustomerData?.call_review) && (
                    <div className="mt-4 p-3 border rounded bg-light">
                      <h6 className="mb-3">Call Review</h6>
                      <p className="mb-1">
                        <strong>Calling Date:</strong>{' '}
                        {formatCallReviewDisplayDate(
                          selectedCustomerData?.call_in_review?.calling_date ||
                            selectedCustomerData?.call_review?.calling_date ||
                            ''
                        ) || 'N/A'}
                      </p>
                      <p className="mb-1">
                        <strong>Review:</strong>{' '}
                        {selectedCustomerData?.call_in_review?.review ||
                          selectedCustomerData?.call_review?.review ||
                          'N/A'}
                      </p>
                      <p className="mb-0">
                        <strong>Is Liked:</strong>{' '}
                        {(() => {
                          const value =
                            selectedCustomerData?.call_in_review?.is_liked ??
                            selectedCustomerData?.call_review?.is_liked;
                          if (typeof value === 'boolean') {
                            return value ? 'Yes' : 'No';
                          }
                          if (value === '' || value === null || typeof value === 'undefined') {
                            return 'N/A';
                          }
                          return String(value);
                        })()}
                      </p>
                    </div>
                  )}
              </div>
              <div className="modal-footer">
                {selectedCustomerData?.customer_phone && (
                  <button type="button" className="btn btn-primary me-2" onClick={handleOpenCallReviewForm}>
                    Log Call Review
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={closeCustomerModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
          </div>
        </>
      )}

      {callReviewModalVisible && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog" role="document">
            <form className="modal-content" onSubmit={handleCallReviewSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">Call Review</h5>
                <button type="button" className="close" onClick={closeCallReviewModal} aria-label="Close" disabled={callReviewSubmitting}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Customer Name</label>
                  <input type="text" className="form-control" value={callReviewCustomerInfo.name} readOnly />
                </div>
                <div className="mb-3">
                  <label className="form-label">Phone Number</label>
                  <div className="input-group">
                    <input type="text" className="form-control" value={callReviewCustomerInfo.phone} readOnly />
                    {callReviewCustomerInfo.phone && (
                      <a className="btn btn-outline-secondary" href={`tel:${callReviewCustomerInfo.phone}`}>
                        Call
                      </a>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="callReviewDate" className="form-label">
                    Calling Date
                  </label>
                  <input
                    id="callReviewDate"
                    type="datetime-local"
                    className="form-control"
                    value={callReviewForm.calling_date}
                    onChange={handleCallReviewFieldChange('calling_date')}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="callReviewNotes" className="form-label">
                    Review
                  </label>
                  <textarea
                    id="callReviewNotes"
                    className="form-control"
                    rows="3"
                    value={callReviewForm.review}
                    onChange={handleCallReviewFieldChange('review')}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="callReviewLiked" className="form-label">
                    Is Liked?
                  </label>
                  <select
                    id="callReviewLiked"
                    className="form-select"
                    value={callReviewForm.is_liked}
                    onChange={handleCallReviewFieldChange('is_liked')}
                  >
                    <option value="">Select...</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeCallReviewModal} disabled={callReviewSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={callReviewSubmitting}>
                  {callReviewSubmitting ? 'Saving...' : 'Save Review'}
                </button>
              </div>
            </form>
          </div>
          </div>
        </>
      )}

      {affectModalVisible && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Affecter la commande</h5>
                <button type="button" className="close" onClick={closeAffectModal} aria-label="Close" disabled={affectSubmitting}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <p className="mb-2">
                    <strong>Commande:</strong> {selectedOrderToAffect?.orderNumber || selectedOrderToAffect?.orderCode}
                  </p>
                  {selectedOrderToAffect?.affected?.user_id && (
                    <div className="alert alert-info mb-3">
                      <small>
                        <strong>Actuellement affectée à:</strong>{' '}
                        {typeof selectedOrderToAffect.affected.user_id === 'object'
                          ? selectedOrderToAffect.affected.user_id?.name || selectedOrderToAffect.affected.user_id?.email
                          : selectedOrderToAffect.affected.user_id}
                        {' '}({selectedOrderToAffect.affected?.status || 'ongoing'})
                      </small>
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="affectUser" className="form-label">
                    Utilisateur <span className="text-danger">*</span>
                  </label>
                  <select
                    id="affectUser"
                    className="form-select"
                    value={affectUserId}
                    onChange={(e) => setAffectUserId(e.target.value)}
                    disabled={affectSubmitting}
                  >
                    <option value="">-- Sélectionner un utilisateur --</option>
                    {usersList.map((user) => (
                      <option key={user._id || user.id} value={user._id || user.id}>
                        {user.name || user.username || user.email} {user.lastname ? user.lastname : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label htmlFor="affectStatus" className="form-label">
                    Statut
                  </label>
                  <select
                    id="affectStatus"
                    className="form-select"
                    value={affectStatus}
                    onChange={(e) => setAffectStatus(e.target.value)}
                    disabled={affectSubmitting}
                  >
                    <option value="ongoing">En cours (Ongoing)</option>
                    <option value="completed">Terminé (Completed)</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeAffectModal}
                  disabled={affectSubmitting}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAffectSubmit}
                  disabled={affectSubmitting || !affectUserId}
                >
                  {affectSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Affectation...
                    </>
                  ) : (
                    'Affecter'
                  )}
                </button>
              </div>
            </div>
          </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrdersGlovo;

